import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Bell, CheckCircle2, ChevronDown, Eye, Flag, LoaderCircle, Lock,
  LogOut, ImagePlus, MessageCircle, Plus, RefreshCw, ShieldCheck, Stethoscope,
  UserPlus, Users, X,
} from "lucide-react";

/**
 * The physician client, slice 1: the case loop.
 *
 * Sign in, verify, pick a room, read the feed, write a case (with a poll, a
 * band restriction, or attested images), read a case's replies, answer, mark
 * what changed your management, see why a post was held and resubmit it, flag
 * what shouldn't be there, and read your notifications. Chat, groups, search
 * and the staff screens are later slices.
 *
 * Mobile-first: one column, thumb-reach actions, no hover-only affordances.
 */

const SESSION_KEY = "medconnect.session";
const KINDS = [
  ["case", "Cases"],
  ["discussion", "Discussions"],
  ["research", "Research"],
  ["job", "Jobs"],
];
const BANDS = ["training", "0-2", "3-5", "6-10", "11-20", "20+"];
const BAND_LABEL = {
  training: "Resident or fellow", "0-2": "0–2 years", "3-5": "3–5 years",
  "6-10": "6–10 years", "11-20": "11–20 years", "20+": "20+ years",
};

/** What each detector means to the person whose post it held. */
const DETECTOR_LABEL = {
  date: "a calendar date", age_over_89: "an age over 89", mrn: "a record number",
  ssn: "a national identifier", phone: "a phone number", email: "an email address",
  url_with_id: "a link that identifies a chart", person_name: "a person's name",
  postal_code: "a postal code", location_detail: "a room or bed number",
  burned_in_text_unverified: "an image that could not be checked",
};
const detectorLabel = (d) =>
  DETECTOR_LABEL[d] ?? (d.startsWith("burned_in_")
    ? `${DETECTOR_LABEL[d.slice("burned_in_".length)] ?? "an identifier"} read from the image`
    : "an identifier");

/**
 * A v4 UUID in any context the client actually runs in.
 *
 * crypto.randomUUID exists only in SECURE contexts — HTTPS, or localhost. The
 * LAN preview is plain HTTP on a LAN address, which is neither, and the first
 * phone to open it threw `crypto.randomUUID is not a function` on its first
 * mutation. crypto.getRandomValues has no such restriction, so the fallback
 * builds the same thing from 16 random bytes with the version and variant
 * bits set. Every idempotency key goes through here; nothing in this file may
 * call crypto.randomUUID directly, and a contract test holds that shut.
 */
function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;   // version 4
  b[8] = (b[8] & 0x3f) | 0x80;   // variant 10xx
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** True inside an installed app — the hint and the sign-in note both hide. */
function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator.standalone === true;
}

/**
 * Visits are counted once per page load (module flag: StrictMode mounts an
 * effect twice, and a double-counted first visit would show the hint to a
 * first-time visitor). The install hint waits for the SECOND visit: someone
 * who came back is someone worth offering a home-screen icon.
 */
let visitCounted = false;
function countVisit() {
  if (visitCounted) return 0;
  visitCounted = true;
  try {
    const n = Number(window.localStorage.getItem("medconnect.visits") ?? "0") + 1;
    window.localStorage.setItem("medconnect.visits", String(n));
    return n;
  } catch { return 0; }
}

/**
 * One line, dismissible, second visit onward, browser only. Android surfaces
 * `beforeinstallprompt`, so the button there triggers the real prompt; Safari
 * offers no prompt at all, so iOS gets the words instead: Share → Add to
 * Home Screen. Anything else gets its browser's own install wording.
 */
/**
 * An in-app browser on iOS — Gmail's WKWebView, Chrome, Firefox, Edge — is
 * where "Add to Home Screen" does not exist and where the session lives in a
 * storage container Safari never sees. Real Safari carries a "Safari/" token
 * and none of the wrapper tokens; every wrapper either drops the token
 * (plain WKWebView) or declares itself (CriOS, FxiOS, EdgiOS, GSA). Verified
 * against UA strings by hand — Playwright cannot drive iOS Safari, and the
 * on-device check stays a manual step (docs/deployment-record.md §9).
 */
function inAppBrowserOnIOS() {
  const ua = window.navigator.userAgent;
  if (!/iPad|iPhone|iPod/.test(ua)) return false;
  return !/Safari\//.test(ua) || /CriOS|FxiOS|EdgiOS|GSA/.test(ua);
}

function InstallHint() {
  const [visits, setVisits] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try { return window.localStorage.getItem("medconnect.installHintDismissed") === "1"; }
    catch { return true; }
  });
  const [installEvent, setInstallEvent] = useState(null);
  useEffect(() => { setVisits(countVisit() || Number(window.localStorage.getItem("medconnect.visits") ?? 0)); }, []);
  useEffect(() => {
    const capture = (event) => { event.preventDefault(); setInstallEvent(event); };
    window.addEventListener("beforeinstallprompt", capture);
    return () => window.removeEventListener("beforeinstallprompt", capture);
  }, []);

  if (dismissed || visits < 2 || isStandalone()) return null;
  const dismiss = () => {
    try { window.localStorage.setItem("medconnect.installHintDismissed", "1"); } catch { /* shown again next load */ }
    setDismissed(true);
  };
  const ios = /iPad|iPhone|iPod/.test(window.navigator.userAgent);
  const inApp = inAppBrowserOnIOS();
  return (
    <div role="note" className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2 text-xs text-slate-600">
      <span className="min-w-0 flex-1 truncate">
        {installEvent
          ? "MedConnect can live on your home screen."
          : inApp
            ? `Open ${window.location.host} in Safari to install MedConnect — this in-app browser can't.`
            : ios
              ? "Add MedConnect to your home screen: Share → Add to Home Screen."
              : "Add MedConnect to your home screen from your browser's menu."}
      </span>
      {installEvent && (
        <button onClick={() => { void installEvent.prompt(); dismiss(); }}
          className="shrink-0 font-semibold text-teal-800">Install</button>
      )}
      <button onClick={dismiss} aria-label="Dismiss install hint"
        className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-200"><X size={13} /></button>
    </div>
  );
}

class ApiError extends Error {
  constructor(status, body) {
    super(body?.message || body?.error || `Request failed (${status})`);
    this.status = status;
    this.body = body;
  }
}

/**
 * localStorage, not sessionStorage: a physician who closes the tab between two
 * ward rounds is the same physician. The token dies on sign-out, on a 401, and
 * nowhere else — in particular a NETWORK failure while restoring must not
 * discard it, which is how "session lost on reload" happened: any error on the
 * restore path wiped the token, so one slow /api/me signed you out.
 */
function storedToken() {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(SESSION_KEY); } catch { return null; }
}
function saveToken(token) {
  if (typeof window === "undefined") return;
  try {
    if (token) window.localStorage.setItem(SESSION_KEY, token);
    else window.localStorage.removeItem(SESSION_KEY);
  } catch { /* private windows may refuse; the session then lasts the page */ }
}

async function api(path, { method = "GET", token, body, headers = {}, signal } = {}) {
  const response = await fetch(path, {
    method,
    signal,
    headers: {
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      // Safe retries on every mutation; ignored by routes that don't replay.
      ...(method !== "GET" ? { "idempotency-key": uuid() } : {}),
      ...headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await response.text();
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; }
  catch { payload = { message: text || "Invalid server response" }; }
  if (!response.ok) throw new ApiError(response.status, payload);
  return payload;
}

/**
 * One arrival, one exchange. React StrictMode runs an effect twice on the same
 * mount, and a one-time link that is consumed twice reports a successful
 * sign-in as an expired link. Keyed by token and never cleared: within one
 * page load a given link has one answer, whoever asks.
 */
const linkExchanges = new Map();
function consumeSignInLink(token) {
  let exchange = linkExchanges.get(token);
  if (!exchange) {
    exchange = api("/api/auth/link/consume", { method: "POST", body: { token } });
    linkExchanges.set(token, exchange);
  }
  return exchange;
}

async function uploadMedia(file, token) {
  const extension = file.name.split(".").pop()?.toLowerCase();
  // Some pickers hand over a file with an empty type. The extension is the
  // only thing left, and a phone photo arriving as application/octet-stream
  // is refused for a reason the author cannot act on.
  const inferred = extension === "dcm" ? "application/dicom"
    : extension === "heic" ? "image/heic"
    : extension === "heif" ? "image/heif"
    : null;
  const mime = file.type || inferred || "application/octet-stream";
  const response = await fetch("/api/uploads", {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/octet-stream",
      "x-media-type": mime,
    },
    body: file,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(response.status, payload);
  return payload;
}

/* ========================================================================== */
/* Root                                                                       */
/* ========================================================================== */

export default function App() {
  const [auth, setAuth] = useState({ state: "loading", token: null, me: null, error: null });
  const [rooms, setRooms] = useState([]);
  const [physicianPath, setPhysicianPath] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api("/api/public/rooms", { signal: controller.signal })
      .then((result) => setRooms(result?.rooms ?? []))
      .catch((error) => { if (error.name !== "AbortError") setRooms([]); });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    let active = true;
    async function restore() {
      const url = new URL(window.location.href);
      const magicToken = url.pathname === "/signin" ? url.searchParams.get("token") : null;
      let token = storedToken();

      if (magicToken) {
        try {
          const consumed = await consumeSignInLink(magicToken);
          token = consumed.token;
          saveToken(token);
          // A magic link tapped in email opens the BROWSER, never the
          // installed app — a PWA platform limitation. Remember the arrival
          // so Home can say, once, what that means for an installed copy.
          try { window.sessionStorage.setItem("medconnect.linkSignin", "1"); } catch { /* cosmetic */ }
          window.history.replaceState({}, "", "/");
        } catch (error) {
          if (active) setAuth({
            state: "signed-out", token: null, me: null,
            error: error.message || "That sign-in link is invalid or expired.",
          });
          return;
        }
      }

      if (!token) {
        if (active) setAuth({ state: "signed-out", token: null, me: null, error: null });
        return;
      }

      try {
        const me = await api("/api/me", { token });
        if (active) setAuth({ state: "signed-in", token, me, error: null });
      } catch (error) {
        // Only a 401 means the session is dead. Anything else — the API
        // restarting, a tunnel dropping — keeps the token and offers a retry;
        // signing a physician out over a network blip is how work gets lost.
        if (error.status === 401) {
          saveToken(null);
          if (active) setAuth({ state: "signed-out", token: null, me: null, error: null });
        } else if (active) {
          setAuth({ state: "unreachable", token, me: null, error: error.message });
        }
      }
    }
    void restore();
    return () => { active = false; };
  }, []);

  const refreshMe = async () => {
    const me = await api("/api/me", { token: auth.token });
    setAuth((current) => ({ ...current, state: "signed-in", me }));
    return me;
  };

  const signOut = async () => {
    try { await api("/api/auth/logout", { method: "POST", token: auth.token }); }
    finally {
      saveToken(null);
      setAuth({ state: "signed-out", token: null, me: null, error: null });
    }
  };

  if (auth.state === "loading") return <CenteredStatus label="Signing you in…" />;
  if (auth.state === "unreachable") {
    return (
      <Shell className="grid place-items-center p-6">
        <div className="max-w-sm text-center">
          <h1 className="font-serif text-2xl">Can't reach MedConnect</h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Your session is still here — the server didn't answer. {auth.error}
          </p>
          <button onClick={() => window.location.reload()}
            className="mt-4 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white">
            Try again
          </button>
        </div>
      </Shell>
    );
  }
  if (auth.state === "signed-out") return <SignIn rooms={rooms} initialError={auth.error} />;
  if (auth.me?.role === "industry" && auth.me?.trust !== "verified") {
    return <IndustryPending me={auth.me} onRefresh={refreshMe} onSignOut={signOut} />;
  }
  if (auth.me?.trust !== "verified" && !auth.me?.display_name && !physicianPath) {
    return <AccountTypeChoice token={auth.token} onPhysician={() => setPhysicianPath(true)}
      onRefresh={refreshMe} onSignOut={signOut} />;
  }
  if (auth.me?.trust !== "verified") {
    return <IdentityVerification token={auth.token} me={auth.me}
      onRefresh={refreshMe} onSignOut={signOut} />;
  }
  // The only paths the client knows are /, /signin (the magic-link landing,
  // rewritten to / on arrival) and, for staff, /staff. Everything else — and
  // /staff for anyone without a staff role — is the SAME "Page not found",
  // exactly as the API answers them: a non-staff physician probing /staff
  // learns nothing a typo would not teach. Authority is not here: every
  // staff route is require_staff-gated in SQL; this only decides what to
  // offer (D35).
  const path = window.location.pathname;
  if (path === "/staff" && auth.me?.staff_role) {
    return <StaffConsole token={auth.token} me={auth.me} onSignOut={signOut} />;
  }
  if (path !== "/" && path !== "/signin") return <NotFound />;
  // Clinical rooms, not venues. Every verified physician holds the
  // industry-visible venue by construction (D13), so "has a room" does not mean
  // "has chosen a room": the picker shows until a CLINICAL room is held,
  // whatever else the membership list contains. Industry accounts have exactly
  // one venue and do not choose it.
  const needsRoom = auth.me?.role === "physician"
    && !auth.me?.rooms?.some((room) => !room.industryVisible);
  if (needsRoom) {
    return <RoomPicker token={auth.token} rooms={rooms} onReady={refreshMe} onSignOut={signOut} />;
  }
  return <Home token={auth.token} me={auth.me} onSignOut={signOut} />;
}

/* ========================================================================== */
/* Frame                                                                      */
/* ========================================================================== */

function Shell({ children, className = "" }) {
  return (
    <div className={`mx-auto min-h-screen max-w-2xl bg-slate-50 text-slate-950 ${className}`}>
      {children}
    </div>
  );
}

function CenteredStatus({ label }) {
  return (
    <Shell className="grid place-items-center p-6">
      <div className="flex items-center gap-3 text-sm text-slate-600">
        <LoaderCircle className="animate-spin" size={20} /> {label}
      </div>
    </Shell>
  );
}

function CenteredInline({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
      <LoaderCircle className="animate-spin" size={16} /> {label}
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2 text-slate-950">
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-teal-800 text-white">
        <Stethoscope size={20} />
      </span>
      <span className="font-serif text-xl tracking-tight">MedConnect</span>
    </div>
  );
}

function QuietSignOut({ onClick }) {
  return (
    <button onClick={onClick} aria-label="Sign out"
      className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100">
      <LogOut size={14} /> Sign out
    </button>
  );
}

/* ========================================================================== */
/* Sign in — the rate limit is a wait, not a retry                            */
/* ========================================================================== */

const RESEND_SECONDS = 60;

function SignIn({ rooms, initialError }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");   // idle | sending | sent
  const [error, setError] = useState(initialError);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown > 0]);

  const submit = async (event) => {
    event.preventDefault();
    if (state === "sending") return;   // a double-tap is one request, not two
    setState("sending");
    setError(null);
    try {
      await api("/api/auth/link", { method: "POST", body: { email } });
      setState("sent");
      setCooldown(RESEND_SECONDS);
    } catch (e) {
      setError(e.message);
      setState("idle");
    }
  };

  return (
    <Shell className="flex flex-col">
      <InstallHint />
      <div className="flex min-h-0 flex-1 flex-col px-5 py-8 sm:px-10">
      <Brand />
      <main className="my-auto py-14">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-800">Verified professional community</p>
        <h1 className="mt-3 max-w-xl font-serif text-4xl leading-tight tracking-tight">
          Clinical discussion with colleagues who do what you do.
        </h1>
        <p className="mt-4 max-w-lg text-sm leading-6 text-slate-600">
          Physicians and approved industry representatives sign in by email. Access is verified before community content is visible.
        </p>

        {rooms.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" aria-label="Open specialty rooms">
            {rooms.map((room) => (
              <span key={room.venue_id} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                {room.name} · {Number(room.members ?? 0).toLocaleString()} physicians
              </span>
            ))}
          </div>
        )}

        {state === "sent" ? (
          <div className="mt-8 max-w-md rounded-xl border border-teal-200 bg-teal-50 p-4" role="status">
            <div className="flex gap-3">
              <CheckCircle2 className="mt-0.5 shrink-0 text-teal-800" size={20} />
              <div>
                <h2 className="font-medium">Check your email</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  If the address can receive mail, a one-time link is on its way. It expires in 15 minutes.
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Not there yet? Wait — don't retry. Each address gets five links an
                  hour, requesting again doesn't speed delivery, and repeated requests
                  lock the address out until the hour turns.
                </p>
                <button
                  disabled={cooldown > 0}
                  onClick={() => { setState("idle"); }}
                  className="mt-3 text-sm font-medium text-teal-800 disabled:cursor-not-allowed disabled:text-slate-400">
                  {cooldown > 0
                    ? `You can request another in ${cooldown}s`
                    : "Request another link"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form className="mt-8 max-w-md" onSubmit={submit}>
            <label className="text-sm font-medium" htmlFor="email">Work email</label>
            <input
              id="email" type="email" required autoComplete="email"
              value={email} onChange={(event) => setEmail(event.target.value)}
              placeholder="you@organization.org"
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 outline-none ring-teal-700 focus:ring-2"
            />
            {error && <p className="mt-2 text-sm text-rose-700" role="alert">{error}</p>}
            <button disabled={state === "sending"}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
              {state === "sending" && <LoaderCircle className="animate-spin" size={16} />}
              Email me a sign-in link
            </button>
          </form>
        )}
      </main>
      <p className="text-xs leading-5 text-slate-500">Not for emergencies or identifiable patient information.</p>
      </div>
    </Shell>
  );
}

/**
 * The line that ends a browser-side magic-link sign-in, shown once, browser
 * only — and split by platform because the truthful sentence differs:
 *
 *   Android  a Chrome-installed app shares the browser profile's storage, so
 *            the session in localStorage IS the installed app's session.
 *   iOS      WebKit gives every Home Screen web app its own isolated storage
 *            container — the Safari session does NOT carry over, and no copy
 *            here may claim it does. The app signs in with its own link.
 *
 * The iOS claim was slated to be verified on a physical iPhone; no device was
 * available, and the platform's documented isolation decides it the same way
 * — so the copy states the limitation instead of the wish.
 */
function AfterLinkSignInNote() {
  const [shown, setShown] = useState(() => {
    try { return window.sessionStorage.getItem("medconnect.linkSignin") === "1"; }
    catch { return false; }
  });
  if (!shown || isStandalone()) return null;
  const dismiss = () => {
    try { window.sessionStorage.removeItem("medconnect.linkSignin"); } catch { /* once per session anyway */ }
    setShown(false);
  };
  const ios = /iPad|iPhone|iPod/.test(window.navigator.userAgent);
  const inApp = inAppBrowserOnIOS();
  return (
    <div role="note" className="flex items-center gap-2 border-b border-teal-200 bg-teal-50 px-4 py-2 text-xs text-teal-900">
      <span className="min-w-0 flex-1">
        {inApp
          ? `You're in your mail app's browser — its sign-in stays here. Open ${window.location.host} in Safari to stay signed in.`
          : ios
            ? "Installed the app? iOS keeps its sign-in separate — open it from your home screen and email yourself a link from there."
            : "Installed the app? Open it from your home screen — you're signed in there too."}
      </span>
      <button onClick={dismiss} aria-label="Dismiss sign-in note"
        className="shrink-0 rounded p-1 text-teal-700 hover:bg-teal-100"><X size={13} /></button>
    </div>
  );
}

/* ========================================================================== */
/* Account type and identity                                                  */
/* ========================================================================== */

function AccountTypeChoice({ token, onPhysician, onRefresh, onSignOut }) {
  const [industry, setIndustry] = useState(false);
  const [form, setForm] = useState({ organizationName: "", website: "", industryType: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      await api("/api/onboarding/industry", { method: "POST", token, body: form });
      await onRefresh();
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Shell className="px-5 py-8 sm:px-10">
      <div className="flex items-center justify-between"><Brand /><QuietSignOut onClick={onSignOut} /></div>
      <main className="mx-auto mt-14 max-w-lg">
        <h1 className="font-serif text-3xl tracking-tight">Choose your account type</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">This choice controls which communities and content your account can access.</p>
        {!industry ? (
          <div className="mt-8 grid gap-3">
            <button onClick={onPhysician} className="rounded-xl border border-slate-200 bg-white p-5 text-left hover:border-teal-700">
              <span className="block font-semibold">Physician</span>
              <span className="mt-1 block text-sm text-slate-600">Verify clinical credentials and join specialty rooms.</span>
            </button>
            <button onClick={() => setIndustry(true)} className="rounded-xl border border-slate-200 bg-white p-5 text-left hover:border-teal-700">
              <span className="block font-semibold">Industry representative</span>
              <span className="mt-1 block text-sm text-slate-600">Apply for limited access to research discussions and job posting.</span>
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <Field label="Organization name" name="organizationName" required value={form.organizationName} onChange={(e) => setForm({ ...form, organizationName: e.target.value })} />
            <Field label="Organization website (https://)" name="website" type="url" pattern="https://.*" required value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <label className="block text-sm font-medium" htmlFor="industryType">Organization type</label>
            <select id="industryType" required value={form.industryType} onChange={(e) => setForm({ ...form, industryType: e.target.value })} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3">
              <option value="">Choose a type</option><option value="pharma">Pharmaceutical</option><option value="biotech">Biotechnology</option><option value="device">Medical device</option><option value="research">Research organization</option><option value="recruiting">Recruiting</option><option value="other">Other</option>
            </select>
            <p className="rounded-xl bg-slate-100 p-3 text-xs leading-5 text-slate-600">Approval does not grant access to clinical cases, general physician discussions, or specialty rooms.</p>
            {error && <p className="text-sm text-rose-700" role="alert">{error}</p>}
            <button disabled={busy} className="w-full rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">Request industry access</button>
          </form>
        )}
      </main>
    </Shell>
  );
}

function IndustryPending({ me, onRefresh, onSignOut }) {
  const status = me.industry_profile?.status ?? "pending";
  return (
    <Shell className="px-5 py-8 sm:px-10">
      <div className="flex items-center justify-between"><Brand /><QuietSignOut onClick={onSignOut} /></div>
      <main className="mx-auto mt-20 max-w-lg rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <h1 className="font-serif text-3xl tracking-tight">Industry access {status}</h1>
        <p className="mt-3 text-sm leading-6 text-slate-700">{me.industry_profile?.organizationName} will receive access only after manual review. Clinical rooms remain unavailable.</p>
        <button onClick={onRefresh} className="mt-5 flex items-center gap-2 text-sm font-semibold text-teal-800"><RefreshCw size={15} /> Check status</button>
      </main>
    </Shell>
  );
}

function IdentityVerification({ token, me, onRefresh, onSignOut }) {
  const [form, setForm] = useState({
    givenName: "", familyName: "", npi: "",
    country: me.country_id ?? "us", institution: me.institution ?? "",
    experienceBand: "", practisedAbroad: false,
  });
  const [state, setState] = useState("idle");   // idle | checking | waiting
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setState("checking");
    setError(null);
    try {
      const response = await api("/api/onboarding/identity", { method: "POST", token, body: form });
      setResult(response);
      if (response.state === "verified") await onRefresh();
      else setState("waiting");
    } catch (e) {
      setError(e.message);
      setState("idle");
    }
  };
  const field = (name) => ({ value: form[name], onChange: (event) => setForm({ ...form, [name]: event.target.value }) });

  return (
    <Shell className="px-5 py-8 sm:px-10">
      <div className="flex items-center justify-between"><Brand /><QuietSignOut onClick={onSignOut} /></div>
      <main className="mx-auto mt-14 max-w-lg">
        <ShieldCheck className="text-teal-800" size={28} />
        <h1 className="mt-4 font-serif text-3xl tracking-tight">Verify your physician identity</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">Community content remains inaccessible until a primary source confirms your identity.</p>

        {state === "waiting" ? (
          <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <h2 className="font-medium">Manual review started</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">{result?.message}</p>
            <div className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
              <p>
                <span className="font-medium">What happens next:</span> a physician
                reviewer checks your licence against your country's register by
                hand, rather than trusting a form.
              </p>
              <p>
                <span className="font-medium">How long:</span> most reviews finish
                within one business day. A senior-experience claim needs a second
                reviewer and can take a day longer.
              </p>
              <p>
                Nothing else is needed from you. You can close this page — sign in
                with a fresh link any time to see where things stand.
              </p>
            </div>
            <button onClick={onRefresh} className="mt-4 flex items-center gap-2 text-sm font-medium text-teal-800"><RefreshCw size={15} /> Check status</button>
          </div>
        ) : (
          <form className="mt-8 space-y-4" onSubmit={submit}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Given name" name="givenName" required {...field("givenName")} />
              <Field label="Family name" name="familyName" required {...field("familyName")} />
            </div>
            <Field label="Institution" name="institution" {...field("institution")} />
            <label className="block text-sm font-medium" htmlFor="country">Country</label>
            <select id="country" {...field("country")} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3">
              <option value="us">United States</option><option value="uk">United Kingdom</option><option value="ca">Canada</option><option value="de">Germany</option><option value="in">India</option>
            </select>
            <label className="block text-sm font-medium" htmlFor="experienceBand">Experience in independent practice</label>
            <select id="experienceBand" required {...field("experienceBand")} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3">
              <option value="">Choose a range</option>
              {BANDS.map((band) => <option key={band} value={band}>{BAND_LABEL[band]}</option>)}
            </select>
            <label className="flex items-start gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.practisedAbroad} onChange={(event) => setForm({ ...form, practisedAbroad: event.target.checked })} className="mt-1" />
              This includes practice in another country
            </label>
            {form.country === "us" && <Field label="NPI (10 digits)" name="npi" inputMode="numeric" pattern="[0-9]{10}" required {...field("npi")} />}
            {error && <p className="text-sm text-rose-700" role="alert">{error}</p>}
            <button disabled={state === "checking"} className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
              {state === "checking" && <LoaderCircle className="animate-spin" size={16} />} Verify identity
            </button>
          </form>
        )}
      </main>
    </Shell>
  );
}

function Field({ label, name, ...props }) {
  return (
    <label className="block text-sm font-medium" htmlFor={name}>
      {label}
      <input id={name} name={name} {...props} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 outline-none ring-teal-700 focus:ring-2" />
    </label>
  );
}

/* ========================================================================== */
/* Room picker                                                                */
/* ========================================================================== */

function RoomPicker({ token, rooms, onReady, onSignOut }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const join = async (venueId) => {
    setBusy(venueId); setError(null);
    try {
      await api("/api/onboarding/room", { method: "POST", token, body: { venueId, focus: [] } });
      await onReady();
    } catch (e) { setError(e.message); setBusy(null); }
  };
  return (
    <Shell className="px-5 py-8 sm:px-10">
      <div className="flex items-center justify-between"><Brand /><QuietSignOut onClick={onSignOut} /></div>
      <main className="mx-auto mt-14 max-w-lg">
        <h1 className="font-serif text-3xl tracking-tight">Choose your first room</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">Start with the specialty where you want to read and contribute.</p>
        <div className="mt-8 space-y-3">
          {rooms.map((room) => (
            <button key={room.venue_id} disabled={Boolean(busy)} onClick={() => join(room.venue_id)}
              className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-teal-700 disabled:opacity-50">
              <span><span className="block font-medium">{room.name}</span><span className="mt-1 block text-xs text-slate-500">{room.blurb}</span></span>
              {busy === room.venue_id ? <LoaderCircle className="animate-spin" size={18} /> : <Plus size={18} />}
            </button>
          ))}
          {!rooms.length && <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">No rooms are available right now.</p>}
        </div>
        {error && <p className="mt-3 text-sm text-rose-700" role="alert">{error}</p>}
      </main>
    </Shell>
  );
}

/* ========================================================================== */
/* Home — feed, case view, held posts, notifications                          */
/* ========================================================================== */

function Home({ token, me, onSignOut }) {
  const clinicalRooms = me.rooms.filter((room) => me.role === "industry" || true);
  const [roomId, setRoomId] = useState(me.rooms[0]?.venueId ?? "");
  const room = me.rooms.find((item) => item.venueId === roomId) ?? me.rooms[0];
  const industryVenue = me.role === "industry" || room?.industryVisible;
  const allowedKinds = industryVenue ? KINDS.filter(([id]) => ["research", "job"].includes(id)) : KINDS;
  const [kind, setKind] = useState(me.role === "industry" ? "research" : "case");
  const [feed, setFeed] = useState({ state: "loading", posts: [], restricted: null, error: null });
  const [view, setView] = useState({ name: "feed" });   // feed | case | held | notifications
  const [compose, setCompose] = useState(false);
  const [held, setHeld] = useState([]);
  const [unread, setUnread] = useState(0);
  // The post just made, while the worker is still checking it. The 202 from
  // POST /api/posts is not "published": the feed shows published posts only,
  // so without this the composer closed into a room that did not contain the
  // post, and the only way to see your own case was a reload (finding 4).
  const [pending, setPending] = useState(null);          // {id, title, kind}
  const [chatUnread, setChatUnread] = useState(0);

  useEffect(() => {
    if (!allowedKinds.some(([id]) => id === kind)) setKind(allowedKinds[0]?.[0] ?? "research");
  }, [roomId]);

  const load = async ({ silent = false } = {}) => {
    if (!roomId) return;
    if (!allowedKinds.some(([id]) => id === kind)) return;
    // A silent load refreshes in place: the publish poll runs every second,
    // and flashing "Loading the room…" once a second reads as a broken page.
    if (!silent) setFeed((current) => ({ ...current, state: "loading", error: null }));
    try {
      const result = await api(`/api/feed?venue=${encodeURIComponent(roomId)}&kind=${encodeURIComponent(kind)}`, { token });
      setFeed({ state: "ready", posts: result.posts ?? [], restricted: result.restricted ?? null, error: null });
      return result.posts ?? [];
    } catch (e) {
      if (!silent) setFeed({ state: "error", posts: [], restricted: null, error: e.message });
      return null;
    }
  };
  useEffect(() => { void load(); }, [roomId, kind]);

  // While a post of yours is being checked, the feed polls — matching the
  // worker's own 2-second claim cadence — and stops the moment the post either
  // publishes (it appears in place) or is held (the banner names it).
  useEffect(() => {
    if (!pending) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      const posts = await load({ silent: true });
      if (cancelled) return;
      if (posts?.some((post) => post.id === pending.id)) { setPending(null); return; }
      try {
        const mine = await api("/api/me/held", { token });
        if (cancelled) return;
        const held = (mine.posts ?? []).find((post) => post.id === pending.id);
        if (held && (held.blocking > 0 || held.findings?.length > 0)) {
          setHeld(mine.posts ?? []);
          setPending(null);
        }
      } catch { /* the next tick asks again */ }
    }, 1000);
    // Thirty seconds is the scan budget; after it, stop claiming progress.
    const stop = setTimeout(() => { setPending(null); void refreshHeld(); }, 30_000);
    return () => { cancelled = true; clearInterval(timer); clearTimeout(stop); };
  }, [pending?.id]);

  const refreshHeld = async () => {
    if (me.role === "industry") return;
    try {
      const result = await api("/api/me/held", { token });
      setHeld(result.posts ?? []);
    } catch { setHeld([]); }
  };
  const refreshUnread = async () => {
    try {
      const result = await api("/api/notifications", { token });
      setUnread((result.notifications ?? []).filter((n) => !n.read_at).length);
    } catch { /* the bell is a convenience, not a control */ }
  };
  const refreshChatUnread = async () => {
    if (me.role === "industry") return;
    try {
      const result = await api("/api/chat/conversations", { token });
      setChatUnread((result.conversations ?? [])
        .reduce((sum, conv) => sum + Math.max(0, Number(conv.unread) || 0), 0));
    } catch { /* same rule as the bell */ }
  };
  useEffect(() => { void refreshHeld(); void refreshUnread(); void refreshChatUnread(); }, []);

  if (view.name === "case") {
    return <CaseView token={token} me={me} post={view.post} focusReplyId={view.focusReplyId}
      onBack={() => { setView({ name: "feed" }); void load(); }}
      onMessage={(conversationId) => setView({ name: "thread", conversationId })} />;
  }
  if (view.name === "case-removed") {
    // The subject of a notification can be gone — removed by its author or a
    // moderator. A plain sentence beats a dead screen (F9).
    return (
      <Shell className="grid place-items-center p-6">
        <div className="max-w-sm text-center">
          <p className="font-serif text-xl">This post was removed.</p>
          <p className="mt-2 text-sm text-slate-600">
            The author or a moderator took it down after this notification was sent.
          </p>
          <button onClick={() => setView({ name: "notifications" })}
            className="mt-4 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white">
            Back to notifications
          </button>
        </div>
      </Shell>
    );
  }
  if (view.name === "held") {
    return <HeldPosts token={token}
      onBack={() => { setView({ name: "feed" }); void refreshHeld(); }}
      onResubmitted={() => { void refreshHeld(); setView({ name: "feed" }); void load(); }} />;
  }
  if (view.name === "notifications") {
    return <Notifications token={token}
      onBack={() => { setView({ name: "feed" }); void refreshUnread(); }}
      onOpenPost={async (postId, focusReplyId) => {
        try {
          const result = await api(`/api/posts/${postId}`, { token });
          setView({ name: "case", post: result.post, focusReplyId });
        } catch (e) {
          if (e.status === 404) setView({ name: "case-removed" });
          // Anything else: stay on the list; the item stays tappable.
        }
        void refreshUnread();
      }} />;
  }
  if (view.name === "messages") {
    return <Messages token={token} me={me}
      onOpen={(conversationId) => setView({ name: "thread", conversationId })}
      onConnections={() => setView({ name: "connections" })}
      onBack={() => { setView({ name: "feed" }); void refreshChatUnread(); }} />;
  }
  if (view.name === "thread") {
    return <Thread token={token} me={me} conversationId={view.conversationId}
      onBack={() => setView({ name: "messages" })}
      onAccess={() => setView({ name: "access", conversationId: view.conversationId })} />;
  }
  if (view.name === "access") {
    return <AccessLog token={token} conversationId={view.conversationId}
      onBack={() => setView({ name: "thread", conversationId: view.conversationId })} />;
  }
  if (view.name === "connections") {
    return <Connections token={token}
      onMessage={(conversationId) => setView({ name: "thread", conversationId })}
      onBack={() => setView({ name: "messages" })} />;
  }

  return (
    <Shell>
      <InstallHint />
      <AfterLinkSignInNote />
      {me.nameDetection?.fake && (
        <div role="status"
          className="border-b border-amber-300 bg-amber-50 px-4 py-2.5 text-xs leading-5 text-amber-900">
          <strong>Name detection on this preview is a stand-in.</strong>{" "}
          It recognizes only {me.nameDetection.names.join(", ")} — a real
          patient name will NOT be caught here. Dates, record numbers, and ages
          are checked for real.
        </div>
      )}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-teal-800">
              {me.role === "industry" ? "Approved industry access" : "Verified physician community"}
            </p>
            <div className="relative mt-0.5 inline-flex items-center">
              <select aria-label="Specialty room" value={roomId} onChange={(event) => setRoomId(event.target.value)}
                className="appearance-none bg-transparent pr-6 font-serif text-xl outline-none">
                {me.rooms.map((item) => <option key={item.venueId} value={item.venueId}>{item.name}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-0" size={15} />
            </div>
          </div>
          <div className="flex items-center gap-1">
            {me.role !== "industry" && (
              <button onClick={() => setView({ name: "messages" })} aria-label="Messages"
                className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100">
                <MessageCircle size={18} />
                {chatUnread > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white">
                    {chatUnread}
                  </span>
                )}
              </button>
            )}
            {me.staff_role && (
              <button onClick={() => { window.location.href = "/staff"; }} aria-label="Staff console"
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
                <ShieldCheck size={18} />
              </button>
            )}
            <button onClick={() => setView({ name: "notifications" })} aria-label="Notifications"
              className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100">
              <Bell size={18} />
              {unread > 0 && (
                <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white">
                  {unread}
                </span>
              )}
            </button>
            <QuietSignOut onClick={onSignOut} />
          </div>
        </div>
        <nav className="mt-4 flex gap-1 overflow-x-auto" aria-label="Post type">
          {allowedKinds.map(([id, label]) => (
            <button key={id} onClick={() => setKind(id)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium ${kind === id ? "bg-slate-950 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="space-y-3 p-4 pb-24">
        {held.length > 0 && (
          <button onClick={() => setView({ name: "held" })}
            className="flex w-full items-center justify-between rounded-xl border border-amber-300 bg-amber-50 p-4 text-left"
            aria-label="Posts under review">
            <span className="text-sm">
              <span className="font-semibold">{held.length} of your posts {held.length === 1 ? "is" : "are"} waiting on review.</span>
              <span className="ml-1 text-slate-600">See what was found and resubmit.</span>
            </span>
            <ArrowLeft className="rotate-180 text-amber-700" size={16} />
          </button>
        )}
        {pending && (
          <div role="status"
            className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
            <LoaderCircle className="animate-spin shrink-0 text-teal-800" size={16} />
            <span>
              <span className="font-medium text-slate-950">{pending.title}</span>
              {" "}is being checked for identifiers — it appears here the moment it clears.
            </span>
          </div>
        )}
        {feed.state === "loading" && <CenteredInline label="Loading the room…" />}
        {feed.state === "error" && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            {feed.error}<button onClick={load} className="ml-2 font-semibold underline">Retry</button>
          </div>
        )}
        {feed.state === "ready" && feed.posts.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <MessageCircle className="mx-auto text-slate-400" size={24} />
            <h2 className="mt-3 font-medium">No {KINDS.find(([id]) => id === kind)?.[1].toLowerCase()} yet</h2>
            <p className="mt-1 text-sm text-slate-500">Start the first useful conversation in {room?.name}.</p>
          </div>
        )}
        {feed.posts.map((post) => (
          <PostCard key={post.id} post={post} token={token} me={me}
            onOpen={() => setView({ name: "case", post })} />
        ))}
        {Number(feed.restricted?.hidden ?? 0) > 0 && (
          <p className="px-2 text-xs text-slate-500">
            {feed.restricted.hidden} additional post{Number(feed.restricted.hidden) === 1 ? " is" : "s are"} restricted by its author
            {feed.restricted.lowestBand ? ` — the nearest opens at ${feed.restricted.lowestBand}` : ""}.
          </p>
        )}
      </main>

      <button onClick={() => setCompose(true)}
        className="fixed bottom-5 right-5 flex items-center gap-2 rounded-full bg-teal-800 px-5 py-3 text-sm font-semibold text-white shadow-lg sm:right-[max(1.25rem,calc((100vw-42rem)/2+1.25rem))]">
        <Plus size={18} /> New post
      </button>
      {compose && (
        <Composer token={token} me={me} venueId={roomId} defaultKind={kind}
          allowedKinds={allowedKinds} industry={industryVenue}
          attachmentsEnabled={me.attachmentsEnabled !== false}
          onClose={() => setCompose(false)}
          onCreated={(created, draft) => {
            setCompose(false);
            // Follow the post: a discussion posted from the Cases tab would
            // otherwise clear its check into a tab the author isn't looking
            // at, which reads exactly like the post vanishing.
            if (draft?.kind && allowedKinds.some(([id]) => id === draft.kind)) setKind(draft.kind);
            if (created?.id) setPending({ id: created.id, title: draft?.title, kind: draft?.kind });
            void load({ silent: true });
          }} />
      )}
    </Shell>
  );
}

/* ========================================================================== */
/* Cards, flags, media                                                        */
/* ========================================================================== */

function PostCard({ post, token, me, onOpen, detail = false }) {
  const [poll, setPoll] = useState(post.poll);
  const [voting, setVoting] = useState(null);
  const [voteError, setVoteError] = useState(null);
  useEffect(() => setPoll(post.poll), [post.poll]);

  const vote = async (optionId) => {
    if (voting) return;
    setVoting(optionId); setVoteError(null);
    try {
      const result = await api(`/api/posts/${post.id}/poll/vote`, {
        method: "POST", token, body: { optionId },
      });
      setPoll(result.poll);
    } catch (e) { setVoteError(e.message); }
    finally { setVoting(null); }
  };

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-slate-500">{post.venue?.name}</p>
        <div className="flex items-center gap-2">
          {post.minBand && <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] text-slate-600">{post.minBand} and above</span>}
          <FlagButton token={token} post={post} me={me} />
        </div>
      </div>
      {detail ? (
        <h1 className="mt-2 font-serif text-2xl leading-snug">{post.headline}</h1>
      ) : (
        <h2 className="mt-2 font-serif text-xl leading-snug">
          <button onClick={onOpen} className="text-left hover:underline">{post.headline}</button>
        </h2>
      )}
      {post.body && <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">{post.body}</p>}
      {post.media?.length > 0 && (
        <div className="mt-4 grid gap-2" aria-label="Post media">
          {post.media.map((item) => <AuthenticatedMedia key={item.id} item={item} token={token} />)}
        </div>
      )}
      {poll && (
        <section className="mt-4 space-y-2" aria-label="Poll">
          {poll.options.map((option) => {
            const percent = poll.totalVotes > 0
              ? Math.round((option.voteCount / poll.totalVotes) * 100) : 0;
            return (
              <button key={option.id} type="button" disabled={Boolean(voting)}
                onClick={() => vote(option.id)} aria-pressed={option.selected}
                className={`relative flex w-full items-center justify-between overflow-hidden rounded-lg border px-3 py-2.5 text-left text-sm disabled:cursor-wait ${option.selected ? "border-teal-700 text-teal-950" : "border-slate-200 text-slate-700 hover:border-slate-400"}`}>
                <span aria-hidden="true" className={`absolute inset-y-0 left-0 ${option.selected ? "bg-teal-100" : "bg-slate-100"}`} style={{ width: `${percent}%` }} />
                <span className="relative pr-3 font-medium">{option.label}</span>
                <span className="relative shrink-0 text-xs tabular-nums">{voting === option.id ? "Voting…" : `${option.voteCount} · ${percent}%`}</span>
              </button>
            );
          })}
          <p className="text-xs text-slate-500">{poll.totalVotes} vote{poll.totalVotes === 1 ? "" : "s"} · Your choice is private</p>
          {voteError && <p className="text-xs text-rose-700" role="alert">{voteError}</p>}
        </section>
      )}
      <div className="mt-4 flex items-end justify-between gap-3 border-t border-slate-100 pt-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{post.author?.name}</p>
          <p className="truncate text-xs text-slate-500">{[post.author?.band, post.author?.site].filter(Boolean).join(" · ")}</p>
        </div>
        {!detail && (
          <button onClick={onOpen} className="shrink-0 text-xs font-medium text-teal-800">
            {post.counts?.replies ?? 0} repl{Number(post.counts?.replies ?? 0) === 1 ? "y" : "ies"} →
          </button>
        )}
      </div>
    </article>
  );
}

/**
 * Two reasons, deliberately. "Patient information" is the safety path — it can
 * hide the post on sight (raise_flag, D36) — and "Other concern" is everything
 * a moderator should look at without a taxonomy quiz in the way.
 */
function FlagButton({ token, post, me }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle");   // idle | sending | filed | hidden
  const [error, setError] = useState(null);
  // The optional note travels with "Other concern" only: the patient-info
  // path acts on sight and needs no essay, and a free-text field there would
  // invite writing the identifier out (which the API refuses anyway).
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState("");
  if (post.author?.id && me?.id && post.author.id === me.id) return null;

  const file = async (reason, withNote) => {
    setState("sending"); setError(null);
    try {
      const result = await api(`/api/posts/${post.id}/flags`, {
        method: "POST", token,
        body: { reason, ...(withNote?.trim() ? { note: withNote.trim() } : {}) },
      });
      setState(result.hidden ? "hidden" : "filed");
    } catch (e) { setError(e.message); setState("idle"); }
  };

  if (state === "filed" || state === "hidden") {
    return (
      <span className="text-[10px] text-slate-500" role="status">
        {state === "hidden" ? "Hidden pending review" : "Reported"}
      </span>
    );
  }
  return (
    <span className="relative">
      {/*
        * Labelled and 44px, not a 14px grey glyph in a 26px target: the phone
        * walkthrough's operator looked for a way to report a post and found
        * nothing (finding 6) — the control was there, invisible. It renders on
        * every post that is not the reader's own, in the feed and in the case
        * view alike; the null return above is the own-post suppression.
        */}
      <button onClick={() => setOpen((v) => !v)} aria-label="Report this post"
        className="flex min-h-11 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700">
        <Flag size={14} /> Report
      </button>
      {open && (
        <span className="absolute right-0 top-8 z-30 w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
          <button onClick={() => file("patient_info")} disabled={state === "sending"}
            className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-50">
            <span className="block font-medium">Patient information</span>
            <span className="block text-xs text-slate-500">A name, date, number, or face is visible.</span>
            <span className="block text-xs text-slate-400">May hide the post immediately; a moderator reviews either way.</span>
          </button>
          {!noting ? (
            <button onClick={() => setNoting(true)} disabled={state === "sending"}
              className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-50">
              <span className="block font-medium">Other concern</span>
              <span className="block text-xs text-slate-500">A moderator should look at this.</span>
              <span className="block text-xs text-slate-400">Goes to the moderator queue; the post stays up meanwhile.</span>
            </button>
          ) : (
            <span className="block px-3 py-2">
              <label className="block text-xs font-medium" htmlFor={`flag-note-${post.id}`}>
                Anything the moderator should know? <span className="font-normal text-slate-400">Optional — don't repeat an identifier.</span>
              </label>
              <textarea id={`flag-note-${post.id}`} rows={2} maxLength={1000} value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1 w-full resize-none rounded-lg border border-slate-300 px-2 py-1.5 text-xs" />
              <button onClick={() => file("other", note)} disabled={state === "sending"}
                className="mt-1 w-full rounded-lg bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                Report to a moderator
              </button>
            </span>
          )}
          {error && <span className="block px-3 py-1 text-xs text-rose-700">{error}</span>}
        </span>
      )}
    </span>
  );
}

function AuthenticatedMedia({ item, token }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = null;
    fetch(item.url, {
      signal: controller.signal,
      headers: { authorization: `Bearer ${token}` },
    }).then((response) => {
      if (!response.ok) throw new Error("media unavailable");
      return response.blob();
    }).then((blob) => {
      objectUrl = URL.createObjectURL(blob);
      setSrc(objectUrl);
    }).catch((error) => {
      if (error.name !== "AbortError") setFailed(true);
    });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.url, token]);

  if (failed) return <p className="rounded-lg bg-slate-100 p-3 text-xs text-slate-600">Media is temporarily unavailable.</p>;
  if (!src) return <div className="flex h-36 items-center justify-center rounded-xl bg-slate-100 text-xs text-slate-500"><LoaderCircle className="mr-2 animate-spin" size={15} /> Loading media…</div>;
  return <img src={src} alt={item.fileName || "Clinical image supplied by the author"} className="max-h-[32rem] w-full rounded-xl bg-slate-100 object-contain" />;
}

/* ========================================================================== */
/* Case view — replies, flat and chronological                                */
/* ========================================================================== */

/**
 * Connect / Message on the author card. Absent on one's own posts and on
 * anonymous ones — an anonymous author cannot be connected with or messaged;
 * that is what anonymity means here (db/anonymous.sql, is_anonymous_author).
 */
function ConnectControl({ token, post, me, onMessage }) {
  const [state, setState] = useState("loading");   // loading | none | outgoing | connected | self
  const [error, setError] = useState(null);
  const authorId = post.author?.id;

  useEffect(() => {
    let active = true;
    if (!authorId || post.anonymous || authorId === me?.id) { setState("self"); return undefined; }
    api("/api/connections", { token })
      .then((result) => {
        if (!active) return;
        if (result.connections.some((p) => p.id === authorId)) setState("connected");
        else if (result.outgoingIds.includes(authorId)) setState("outgoing");
        else if (result.incoming.some((p) => p.id === authorId)) setState("incoming");
        else setState("none");
      })
      .catch(() => { if (active) setState("none"); });
    return () => { active = false; };
  }, [authorId]);

  if (state === "loading" || state === "self") return null;
  const request = async () => {
    setError(null);
    try {
      const result = await api("/api/connections", { method: "POST", token, body: { toId: authorId } });
      setState(result.state === "accepted" ? "connected" : "outgoing");
    } catch (e) { setError(e.message); }
  };
  const message = async () => {
    setError(null);
    try {
      const result = await api(`/api/chat/direct/${authorId}`, { method: "POST", token, body: {} });
      onMessage(result.conversationId);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="flex items-center gap-2">
      {state === "connected" && (
        <button onClick={message}
          className="flex min-h-11 items-center gap-1.5 rounded-xl border border-teal-700 px-3 text-xs font-semibold text-teal-800">
          <MessageCircle size={14} /> Message
        </button>
      )}
      {state === "none" && (
        <button onClick={request}
          className="flex min-h-11 items-center gap-1.5 rounded-xl border border-slate-300 px-3 text-xs font-medium text-slate-600 hover:border-teal-700 hover:text-teal-800">
          <UserPlus size={14} /> Connect
        </button>
      )}
      {state === "outgoing" && <span className="text-xs text-slate-500">Connection requested</span>}
      {state === "incoming" && <span className="text-xs text-slate-500">They asked to connect — see Messages</span>}
      {error && <span className="text-xs text-rose-700" role="alert">{error}</span>}
    </div>
  );
}

function CaseView({ token, me, post, onBack, onMessage, focusReplyId = null }) {
  const [replies, setReplies] = useState({ state: "loading", items: [], error: null });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const mine = post.author?.id && me?.id && post.author.id === me.id;

  const load = async () => {
    setReplies((current) => ({ ...current, state: "loading", error: null }));
    try {
      const result = await api(`/api/posts/${post.id}/replies`, { token });
      setReplies({ state: "ready", items: result.replies ?? [], error: null });
    } catch (e) {
      setReplies({ state: "error", items: [], error: e.message });
    }
  };
  useEffect(() => { void load(); }, [post.id]);

  // Arriving from a notification: bring the reply into view once it exists.
  // One shot, after the list renders; a reply since deleted simply leaves the
  // reader at the case, which is the right fallback.
  useEffect(() => {
    if (!focusReplyId || replies.state !== "ready") return;
    const el = document.getElementById(`reply-${focusReplyId}`);
    if (el) el.scrollIntoView({ block: "center" });
  }, [focusReplyId, replies.state]);

  const send = async (event) => {
    event.preventDefault();
    if (sending || !draft.trim()) return;
    setSending(true); setReplyError(null);
    try {
      await api(`/api/posts/${post.id}/replies`, { method: "POST", token, body: { body: draft } });
      setDraft("");
      await load();
    } catch (e) {
      // 422 identifiers_present arrives with a message worth showing verbatim.
      setReplyError(e.message);
    } finally {
      setSending(false);
    }
  };

  const mark = async (reply) => {
    try {
      await api(`/api/replies/${reply.id}/changed-management`, { method: "POST", token, body: {} });
      setReplies((current) => ({
        ...current,
        items: current.items.map((item) =>
          item.id === reply.id ? { ...item, changed_management: true } : item),
      }));
    } catch (e) {
      setReplyError(e.message);
    }
  };

  return (
    <Shell>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={onBack} aria-label="Back to the room" className="rounded-lg p-1.5 hover:bg-slate-100">
          <ArrowLeft size={18} />
        </button>
        <p className="min-w-0 flex-1 truncate font-medium">{post.venue?.name}</p>
        {/*
          * The author's own way out (D52). The confirm is the whole ceremony:
          * removal is the fastest path off the room when you notice your own
          * mistake, and a second screen between noticing and gone would be
          * friction exactly where speed is the point.
          */}
        {mine && (
          <button
            onClick={async () => {
              if (!window.confirm("Remove this post from the room? Replies go with it. This cannot be undone.")) return;
              try {
                await api(`/api/posts/${post.id}`, { method: "DELETE", token });
                onBack();
              } catch (e) { window.alert(e.message); }
            }}
            className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50">
            Remove
          </button>
        )}
      </header>
      <main className="space-y-3 p-4 pb-28">
        <PostCard post={post} token={token} me={me} detail />
        <ConnectControl token={token} post={post} me={me} onMessage={onMessage} />

        <section aria-label="Replies" className="space-y-3">
          {replies.state === "loading" && <CenteredInline label="Loading replies…" />}
          {replies.state === "error" && (
            <p className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              {replies.error} <button onClick={load} className="font-semibold underline">Retry</button>
            </p>
          )}
          {replies.state === "ready" && replies.items.length === 0 && (
            <p className="px-2 text-sm text-slate-500">No replies yet. What would you do here?</p>
          )}
          {replies.items.map((reply) => (
            <article key={reply.id} id={`reply-${reply.id}`}
              className={`rounded-xl border bg-white p-4 ${reply.id === focusReplyId ? "border-teal-400 ring-1 ring-teal-200" : "border-slate-200"}`}>
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-sm font-medium">{reply.author_name}</p>
                <p className="shrink-0 text-xs text-slate-500">{reply.author_band}</p>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{reply.body}</p>
              {reply.changed_management ? (
                <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-800">
                  <CheckCircle2 size={13} /> Changed the author's management
                </p>
              ) : mine && reply.author_id !== me.id ? (
                <button onClick={() => mark(reply)}
                  className="mt-3 rounded-full border border-teal-700 px-3 py-1 text-xs font-medium text-teal-800 hover:bg-teal-50">
                  This changed my management
                </button>
              ) : null}
            </article>
          ))}
        </section>
      </main>
      <form onSubmit={send}
        className="fixed inset-x-0 bottom-0 z-20 mx-auto flex max-w-2xl items-end gap-2 border-t border-slate-200 bg-white p-3">
        <label className="sr-only" htmlFor="reply">Your reply</label>
        <textarea id="reply" rows={2} value={draft} onChange={(event) => setDraft(event.target.value)}
          placeholder="What would you do? No names, dates, or record numbers."
          className="min-h-[2.75rem] flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none ring-teal-700 focus:ring-2" />
        <button disabled={sending || !draft.trim()} aria-label="Send reply"
          className="grid h-11 w-11 place-items-center rounded-xl bg-teal-800 text-white disabled:opacity-50">
          {sending ? <LoaderCircle className="animate-spin" size={17} /> : <MessageCircle size={17} />}
        </button>
      </form>
      {replyError && (
        <p className="fixed inset-x-0 bottom-20 mx-auto max-w-2xl px-4" role="alert">
          <span className="block rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{replyError}</span>
        </p>
      )}
    </Shell>
  );
}

/* ========================================================================== */
/* Held posts — what was found, and the way back                              */
/* ========================================================================== */

function HeldPosts({ token, onBack, onResubmitted }) {
  const [editing, setEditing] = useState(null);
  // Fetched on open, not passed in: the banner's count can predate the
  // worker's verdict, and this screen exists to show the verdict.
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    let active = true;
    api("/api/me/held", { token })
      .then((result) => { if (active) setPosts(result.posts ?? []); })
      .catch(() => { if (active) setPosts([]); });
    return () => { active = false; };
  }, [token]);
  if (posts === null) {
    return (
      <Shell><CenteredInline label="Loading held posts…" /></Shell>
    );
  }
  if (editing) {
    return <ResubmitForm token={token} post={editing}
      onBack={() => setEditing(null)} onDone={onResubmitted} />;
  }
  return (
    <Shell>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={onBack} aria-label="Back to the room" className="rounded-lg p-1.5 hover:bg-slate-100">
          <ArrowLeft size={18} />
        </button>
        <p className="font-medium">Posts under review</p>
      </header>
      <main className="space-y-3 p-4">
        <p className="text-sm leading-6 text-slate-600">
          A held post is a conversation, not a rejection: it names what the scanner
          found so you can take it out. Nothing here is visible to the room.
        </p>
        {posts.map((post) => <HeldCard key={post.id} post={post} token={token} onEdit={() => setEditing(post)} />)}
        {posts.length === 0 && <p className="px-2 text-sm text-slate-500">Nothing is waiting on review.</p>}
      </main>
    </Shell>
  );
}

/**
 * The author's own text with each blocking finding marked in place.
 *
 * Span offsets are over `title\n\nbody`, exactly as gatePublish scans it, so a
 * span belongs to the title when it ends before the title does and to the body
 * shifted by title.length + 2 otherwise. Zero-length spans (scan failures,
 * image findings) mark nothing.
 */
function highlightSegments(text, offset, findings) {
  const spans = findings
    .filter((f) => !f.attachmentId && f.severity === "block"
      && typeof f.start === "number" && f.end > f.start)
    .map((f) => ({ start: f.start - offset, end: f.end - offset }))
    .filter((sp) => sp.end > 0 && sp.start < text.length)
    .map((sp) => ({ start: Math.max(0, sp.start), end: Math.min(text.length, sp.end) }))
    .sort((a, b) => a.start - b.start);
  const segments = [];
  let at = 0;
  for (const sp of spans) {
    if (sp.start > at) segments.push({ text: text.slice(at, sp.start), marked: false });
    if (sp.end > Math.max(at, sp.start)) {
      segments.push({ text: text.slice(Math.max(at, sp.start), sp.end), marked: true });
      at = sp.end;
    }
  }
  if (at < text.length) segments.push({ text: text.slice(at), marked: false });
  return segments;
}

function HighlightedText({ text, offset, findings, className }) {
  return (
    <p className={className}>
      {highlightSegments(text ?? "", offset, findings).map((seg, i) =>
        seg.marked
          ? <mark key={i} className="rounded bg-amber-200 px-0.5">{seg.text}</mark>
          : <React.Fragment key={i}>{seg.text}</React.Fragment>)}
    </p>
  );
}

function HeldCard({ post, token, onEdit }) {
  /*
   * Three states, and the old code could tell only two apart.
   *
   * `scanning` used to mean "no findings yet", which is also true of a post a
   * COLLEAGUE reported: auto-hide records no deident_findings (the scanner
   * found nothing — a person objected), so an auto-hidden post sat under a
   * spinner saying "Still being checked…" forever, with no way forward. It is
   * the status that decides now, not the absence of findings.
   *
   * Reported is `blocking === 0` rather than `findings.length === 0`: a post
   * can carry warn-severity findings from its own clean scan (attestation,
   * metadata) and then be reported. Nothing the scanner BLOCKED on is the
   * honest test for "there is nothing here for the author to fix".
   */
  const scanning = post.status === "scanning";
  const reported = !scanning && post.blocking === 0;
  const bodyOffset = (post.title?.length ?? 0) + 2;
  // Findings land per attachment (D48); each offending frame is shown to its
  // author — the sanitized derivative, served only while the post is held.
  const imageFindings = [];
  const seen = new Set();
  for (const f of post.findings) {
    if (f.attachmentId && f.severity === "block" && !seen.has(f.attachmentId)) {
      seen.add(f.attachmentId);
      imageFindings.push(f);
    }
  }
  return (
    <article className="rounded-xl border border-amber-200 bg-white p-4">
      <HighlightedText text={post.title} offset={0} findings={post.findings}
        className="font-serif text-lg leading-snug" />
      {scanning ? (
        <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <LoaderCircle className="animate-spin" size={14} /> Checking your post…
        </p>
      ) : reported ? (
        /*
         * No spinner: nothing is processing. No edit-and-resubmit: there is
         * nothing for the author to fix, and the moderator decides. The
         * reason is surfaced when the author should see it, worded as a nudge
         * rather than a verdict — it must not prejudge the review.
         */
        <div className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
          <p>
            A colleague flagged this post for review. A moderator is looking at
            it — nothing here is visible to the room.
          </p>
          {post.report_reason === "patient_info" && (
            <p className="rounded-lg bg-amber-50 p-3 text-xs leading-5 text-amber-900">
              Flagged for possible patient information — please check your images.
            </p>
          )}
        </div>
      ) : (
        <>
          {post.body && (
            <HighlightedText text={post.body} offset={bodyOffset} findings={post.findings}
              className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700" />
          )}
          <ul className="mt-3 space-y-2">
            {post.findings.filter((f) => f.severity === "block").map((f, i) => (
              <li key={i} className="rounded-lg bg-amber-50 p-3 text-sm">
                <span className="font-medium">
                  {f.attachmentId
                    ? `${f.fileName ?? "An image"}: `
                    : "In the text: "}
                  {detectorLabel(f.detector)}
                </span>
                {f.sample && <span className="mt-0.5 block text-xs text-slate-600">{f.sample}</span>}
              </li>
            ))}
          </ul>
          {imageFindings.length > 0 && (
            <div className="mt-3 grid gap-2" aria-label="Held images">
              {imageFindings.map((f) => (
                <figure key={f.attachmentId}>
                  <AuthenticatedMedia token={token}
                    item={{ url: `/api/media/${f.attachmentId}`, kind: "image", fileName: f.fileName }} />
                  <figcaption className="mt-1 text-xs text-slate-600">
                    {f.fileName} — {detectorLabel(f.detector)}. Only you can see this
                    frame, and only while the post is held.
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
          <button onClick={onEdit}
            className="mt-4 w-full rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white">
            Edit and resubmit
          </button>
        </>
      )}
    </article>
  );
}

function ResubmitForm({ token, post, onBack, onDone }) {
  const [title, setTitle] = useState(post.title ?? "");
  const [body, setBody] = useState(post.body ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Attachments the author is giving up on. Dropping the offending frame is
  // usually the right call for a burned-in banner — cropping a clinical image
  // on their behalf is not ours to do (api/held.ts says the same).
  const [dropped, setDropped] = useState(() => new Set());
  // A replacement is NEW pixels nobody has attested to: it goes through the
  // same viewed-then-attested gate as the composer's media (D10), over the
  // new files only — the kept frames carry their original attestation.
  const [newFiles, setNewFiles] = useState([]);
  const [mediaAttested, setMediaAttested] = useState(false);
  const [stage, setStage] = useState("edit");   // edit | attest
  const attachments = post.attachments ?? [];
  const heldOn = new Set((post.findings ?? [])
    .filter((f) => f.attachmentId && f.severity === "block")
    .map((f) => f.attachmentId));

  const toggleDrop = (id) => setDropped((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const addFiles = (list) => {
    setNewFiles((current) => [...current, ...Array.from(list ?? [])]);
    setMediaAttested(false);
  };

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const addAttachments = [];
      for (const file of newFiles) {
        const uploaded = await uploadMedia(file, token);
        addAttachments.push(uploaded.id);
      }
      await api(`/api/posts/${post.id}/resubmit`, {
        method: "POST", token,
        body: {
          title, body,
          ...(dropped.size ? { dropAttachments: [...dropped] } : {}),
          ...(addAttachments.length ? { addAttachments, mediaAttested: true } : {}),
          ...(post.poll_options ? { pollOptions: post.poll_options } : {}),
        },
      });
      onDone();
    } catch (e) { setError(e.message); setBusy(false); setStage("edit"); }
  };

  const submit = async (event) => {
    event.preventDefault();
    // New frames pass through the attestation step; a text-only edit (or a
    // pure removal) goes straight out.
    if (newFiles.length > 0) { setStage("attest"); return; }
    await send();
  };

  if (stage === "attest") {
    return (
      <MediaAttestation files={newFiles} busy={busy} error={error}
        attested={mediaAttested} onAttest={setMediaAttested}
        onBack={() => setStage("edit")} onConfirm={send} />
    );
  }

  return (
    <Shell>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={onBack} aria-label="Back to held posts" className="rounded-lg p-1.5 hover:bg-slate-100">
          <ArrowLeft size={18} />
        </button>
        <p className="font-medium">Edit and resubmit</p>
      </header>
      <main className="p-4">
        <form onSubmit={submit} className="space-y-4">
          <label className="block text-sm font-medium" htmlFor="resubmit-title">Title
            <input id="resubmit-title" required value={title} onChange={(e) => setTitle(e.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3" />
          </label>
          <label className="block text-sm font-medium" htmlFor="resubmit-body">Case text
            <textarea id="resubmit-body" required rows={8} value={body} onChange={(e) => setBody(e.target.value)}
              className="mt-2 w-full resize-none rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm" />
          </label>
          {attachments.length > 0 && (
            <fieldset>
              <legend className="text-sm font-medium">Images on this post</legend>
              <ul className="mt-2 space-y-2">
                {attachments.map((a) => (
                  <li key={a.id}
                    className={`flex items-center justify-between gap-3 rounded-xl border p-3 text-sm ${dropped.has(a.id) ? "border-slate-200 bg-slate-50 text-slate-400" : "border-slate-300 bg-white"}`}>
                    <span className="min-w-0">
                      <span className={`block truncate ${dropped.has(a.id) ? "line-through" : ""}`}>{a.fileName}</span>
                      {heldOn.has(a.id) && !dropped.has(a.id) && (
                        <span className="block text-xs text-amber-700">This is the frame the hold names.</span>
                      )}
                    </span>
                    <button type="button" onClick={() => toggleDrop(a.id)}
                      className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100">
                      {dropped.has(a.id) ? "Keep it" : "Remove"}
                    </button>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}
          <label className="block text-sm font-medium">
            Attach a replacement image
            <input type="file" accept="image/*" multiple
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              className="mt-2 block w-full text-xs text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-semibold" />
            {newFiles.length > 0 && (
              <span className="mt-1 block text-xs text-slate-500">
                {newFiles.map((f) => f.name).join(", ")} — you'll attest to the new
                image{newFiles.length === 1 ? "" : "s"} before resubmitting.
              </span>
            )}
          </label>
          <p className="rounded-xl bg-slate-100 p-3 text-xs leading-5 text-slate-600">
            This is your held text — take out what the scanner named and keep the
            clinical question. Everything is re-checked before it reaches the room.
          </p>
          {error && <p className="text-sm text-rose-700" role="alert">{error}</p>}
          <button disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
            {busy && <LoaderCircle className="animate-spin" size={16} />} Resubmit for checking
          </button>
        </form>
      </main>
    </Shell>
  );
}

/* ========================================================================== */
/* Messages and connections — slice 2                                         */
/* ========================================================================== */

/**
 * The D11 disclosure, in the three places the design promises: the first open
 * of Messages, the compose row, and the conversation header. One sentence,
 * worded plainly, because the trade is real: scanning and legal holds are
 * possible BECAUSE chat is not end-to-end encrypted, and pretending otherwise
 * would be the worst version of it.
 */
const CHAT_DISCLOSURE = "Messages are not end-to-end encrypted. Staff access is logged and visible to both of you.";

function ChatFirstOpenNotice({ onAcknowledge }) {
  return (
    <div className="rounded-xl border border-slate-300 bg-white p-4" role="note">
      <p className="flex items-center gap-2 font-medium"><Lock size={15} className="text-slate-500" /> Before your first message</p>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Chat is encrypted at rest but <span className="font-medium">not end-to-end</span>:
        MedConnect staff can access a conversation for an abuse report or a legal
        hold. Every staff access is logged, and the log is visible to both
        participants — open it any time from the conversation header.
      </p>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Chat isn't the place for cases. Messages are checked for identifiers
        before they send, and a case belongs in the room where the whole
        subspecialty can weigh in.
      </p>
      <button onClick={onAcknowledge}
        className="mt-3 w-full rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white">
        Understood
      </button>
    </div>
  );
}

function Messages({ token, me, onOpen, onConnections, onBack }) {
  const [conversations, setConversations] = useState(null);
  const [noticeSeen, setNoticeSeen] = useState(() => {
    try { return window.localStorage.getItem("medconnect.chatNoticeSeen") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    let active = true;
    api("/api/chat/conversations", { token })
      .then((result) => { if (active) setConversations(result.conversations ?? []); })
      .catch(() => { if (active) setConversations([]); });
    return () => { active = false; };
  }, [token]);

  const acknowledge = () => {
    try { window.localStorage.setItem("medconnect.chatNoticeSeen", "1"); } catch { /* shown again */ }
    setNoticeSeen(true);
  };

  return (
    <Shell>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={onBack} aria-label="Back to the room" className="rounded-lg p-1.5 hover:bg-slate-100">
          <ArrowLeft size={18} />
        </button>
        <p className="flex-1 font-medium">Messages</p>
        <button onClick={onConnections}
          className="flex min-h-11 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-teal-800 hover:bg-slate-100">
          <Users size={15} /> Connections
        </button>
      </header>
      <main className="space-y-3 p-4">
        {!noticeSeen && <ChatFirstOpenNotice onAcknowledge={acknowledge} />}
        {conversations === null && <CenteredInline label="Loading conversations…" />}
        {conversations?.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <MessageCircle className="mx-auto text-slate-400" size={24} />
            <h2 className="mt-3 font-medium">No conversations yet</h2>
            <p className="mt-1 text-sm text-slate-500">
              Connect with a colleague from their case to start one.
            </p>
          </div>
        )}
        {conversations?.map((conv) => (
          <button key={conv.id} onClick={() => onOpen(conv.id)}
            className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-teal-700">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">
                {(conv.others ?? []).map((o) => o.name).join(", ") || "Conversation"}
              </span>
              {conv.preview && <span className="mt-0.5 block truncate text-sm text-slate-500">{conv.preview}</span>}
            </span>
            {Number(conv.unread) > 0 && (
              <span className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-teal-800 px-1.5 text-[11px] font-semibold text-white">
                {conv.unread}
              </span>
            )}
          </button>
        ))}
      </main>
    </Shell>
  );
}

function Thread({ token, me, conversationId, onBack, onAccess }) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  // The inline refusal: the API's 422, shown IN the composer, draft intact.
  // Nothing was stored — the scan runs before the INSERT (api/chat.ts).
  const [refusal, setRefusal] = useState(null);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const result = await api(`/api/chat/conversations/${conversationId}/messages`, { token });
      setMessages(result.messages ?? []);
      const last = (result.messages ?? []).at(-1);
      if (last) {
        await api(`/api/chat/conversations/${conversationId}/read`, {
          method: "POST", token, body: { seq: last.seq },
        }).catch(() => { /* read marks are best-effort */ });
      }
    } catch { setMessages((current) => current ?? []); }
  };
  useEffect(() => {
    let active = true;
    void load();
    api("/api/chat/conversations", { token })
      .then((result) => {
        if (active) setConversation((result.conversations ?? []).find((c) => c.id === conversationId) ?? null);
      })
      .catch(() => { /* the header degrades to "Conversation" */ });
    // The socket route authenticates by header, which a browser WebSocket
    // cannot send — so the thread polls at the same cadence hospital wifi
    // would sustain anyway. Five seconds, only while the thread is open.
    const timer = setInterval(() => { void load(); }, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [conversationId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages?.length]);

  const send = async (event) => {
    event.preventDefault();
    if (sending || !draft.trim()) return;
    setSending(true); setRefusal(null);
    try {
      await api(`/api/chat/conversations/${conversationId}/messages`, {
        method: "POST", token, body: { body: draft },
      });
      setDraft("");
      await load();
    } catch (e) {
      if (e.status === 422 && e.body?.error === "identifiers_present") setRefusal(e.body);
      else setRefusal({ message: e.message });
    } finally {
      setSending(false);
    }
  };

  const others = (conversation?.others ?? []).map((o) => o.name).join(", ");
  return (
    <Shell className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-2.5 backdrop-blur">
        <div className="flex items-center gap-3">
          <button onClick={onBack} aria-label="Back to messages" className="rounded-lg p-1.5 hover:bg-slate-100">
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{others || "Conversation"}</p>
            <p className="truncate text-[11px] text-slate-500">{CHAT_DISCLOSURE}</p>
          </div>
          <button onClick={onAccess}
            className="flex min-h-11 shrink-0 items-center gap-1 rounded-lg px-2 text-[11px] font-medium text-slate-500 hover:bg-slate-100"
            aria-label="Staff access log">
            <Eye size={13} /> Access log
          </button>
        </div>
      </header>
      <main className="flex-1 space-y-2 p-4 pb-40">
        {messages === null && <CenteredInline label="Loading messages…" />}
        {messages?.length === 0 && (
          <p className="px-2 text-sm text-slate-500">No messages yet. Say hello — without patient details.</p>
        )}
        {messages?.map((message) => {
          const mine = message.sender_id === me.id;
          return (
            <div key={message.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-6 ${mine ? "bg-teal-800 text-white" : "border border-slate-200 bg-white text-slate-800"}`}>
                {!mine && <p className="text-[11px] font-medium text-slate-500">{message.sender_name}</p>}
                <p className="whitespace-pre-wrap">{message.body}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </main>
      <form onSubmit={send}
        className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-2xl border-t border-slate-200 bg-white p-3">
        {refusal && (
          <div className="mb-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm" role="alert">
            <p className="text-slate-800">{refusal.message}</p>
            {refusal.suggested && (
              <button type="button" onClick={() => { setDraft(refusal.suggested); setRefusal(null); }}
                className="mt-2 text-xs font-semibold text-teal-800">
                Use the cleaned draft
              </button>
            )}
          </div>
        )}
        <div className="flex items-end gap-2">
          <label className="sr-only" htmlFor="chat-draft">Your message</label>
          <textarea id="chat-draft" rows={2} value={draft} onChange={(event) => setDraft(event.target.value)}
            placeholder="No patient details — cases belong in the room."
            className="min-h-[2.75rem] flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none ring-teal-700 focus:ring-2" />
          <button disabled={sending || !draft.trim()} aria-label="Send message"
            className="grid h-11 w-11 place-items-center rounded-xl bg-teal-800 text-white disabled:opacity-50">
            {sending ? <LoaderCircle className="animate-spin" size={17} /> : <MessageCircle size={17} />}
          </button>
        </div>
        <p className="mt-1.5 px-1 text-[11px] text-slate-500">{CHAT_DISCLOSURE}</p>
      </form>
    </Shell>
  );
}

/**
 * The record D11 promised: who on staff read this conversation, as which
 * role, on what basis, under which ticket, when. Both participants see the
 * same rows; an empty log is stated as a fact, not hidden.
 */
function AccessLog({ token, conversationId, onBack }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let active = true;
    api(`/api/chat/conversations/${conversationId}/access`, { token })
      .then((result) => { if (active) setRows(result.access ?? []); })
      .catch(() => { if (active) setRows([]); });
    return () => { active = false; };
  }, [conversationId]);
  return (
    <Shell>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={onBack} aria-label="Back to the conversation" className="rounded-lg p-1.5 hover:bg-slate-100">
          <ArrowLeft size={18} />
        </button>
        <p className="font-medium">Staff access log</p>
      </header>
      <main className="space-y-3 p-4">
        <p className="text-sm leading-6 text-slate-600">
          Every staff access to this conversation is recorded here, and both of
          you see the same record. The application can read this log and cannot
          edit it.
        </p>
        {rows === null && <CenteredInline label="Loading the log…" />}
        {rows?.length === 0 && (
          <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
            No staff access has been recorded for this conversation.
          </p>
        )}
        {rows?.map((row, index) => (
          <article key={index} className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
            <p className="font-medium">{row.staff_name}{row.staff_role ? ` · ${row.staff_role}` : ""}</p>
            <p className="mt-1 text-slate-600">
              Basis: {row.basis} · Ticket: {row.ticket}
            </p>
            <p className="mt-1 text-xs text-slate-500">{new Date(row.at).toLocaleString()}</p>
          </article>
        ))}
      </main>
    </Shell>
  );
}

function Connections({ token, onMessage, onBack }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try { setData(await api("/api/connections", { token })); }
    catch (e) { setError(e.message); setData({ connections: [], incoming: [], outgoingIds: [] }); }
  };
  useEffect(() => { void load(); }, []);

  const decide = async (id, decision) => {
    setBusy(id); setError(null);
    try {
      await api(`/api/connections/${id}/${decision}`, { method: "POST", token, body: {} });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };
  const message = async (id) => {
    setBusy(id); setError(null);
    try {
      const result = await api(`/api/chat/direct/${id}`, { method: "POST", token, body: {} });
      onMessage(result.conversationId);
    } catch (e) { setError(e.message); setBusy(null); }
  };

  return (
    <Shell>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={onBack} aria-label="Back to messages" className="rounded-lg p-1.5 hover:bg-slate-100">
          <ArrowLeft size={18} />
        </button>
        <p className="font-medium">Connections</p>
      </header>
      <main className="space-y-4 p-4">
        {error && <p className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" role="alert">{error}</p>}
        {data === null && <CenteredInline label="Loading connections…" />}
        {data && data.incoming.length > 0 && (
          <section aria-label="Connection requests" className="space-y-2">
            <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Requests</h2>
            {data.incoming.map((person) => (
              <article key={person.id} className="rounded-xl border border-teal-200 bg-white p-4">
                <p className="font-medium">{person.display_name}</p>
                <p className="text-xs text-slate-500">{[person.band, person.institution].filter(Boolean).join(" · ")}</p>
                {person.note && <p className="mt-2 rounded-lg bg-slate-50 p-2 text-sm text-slate-700">{person.note}</p>}
                <div className="mt-3 flex gap-2">
                  <button disabled={busy === person.id} onClick={() => decide(person.id, "accept")}
                    className="min-h-11 flex-1 rounded-xl bg-teal-800 px-3 text-sm font-semibold text-white disabled:opacity-50">
                    Accept
                  </button>
                  <button disabled={busy === person.id} onClick={() => decide(person.id, "decline")}
                    className="min-h-11 flex-1 rounded-xl border border-slate-300 px-3 text-sm font-medium text-slate-600 disabled:opacity-50">
                    Decline
                  </button>
                </div>
              </article>
            ))}
          </section>
        )}
        {data && (
          <section aria-label="Your connections" className="space-y-2">
            <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Connected</h2>
            {data.connections.length === 0 && (
              <p className="rounded-xl border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-500">
                No connections yet. Ask to connect from a colleague's case — the
                author card carries the button.
              </p>
            )}
            {data.connections.map((person) => (
              <article key={person.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{person.display_name}</span>
                  <span className="block truncate text-xs text-slate-500">{[person.band, person.institution].filter(Boolean).join(" · ")}</span>
                </span>
                <button disabled={busy === person.id} onClick={() => message(person.id)}
                  className="flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl border border-teal-700 px-3 text-xs font-semibold text-teal-800 disabled:opacity-50">
                  <MessageCircle size={14} /> Message
                </button>
              </article>
            ))}
          </section>
        )}
      </main>
    </Shell>
  );
}

/* ========================================================================== */
/* Notifications                                                              */
/* ========================================================================== */

/** The kinds whose subject is a post; tapping one opens it (F9). */
const REPLY_KINDS = new Set(["reply.to_your_post", "reply.to_thread", "reply.marked_helpful"]);

const NOTIFICATION_COPY = {
  "post.held": (p) => `A post of yours was held before publication.`,
  "post.auto_hidden": (p) => `A colleague reported patient information; your post is hidden while a moderator reviews.`,
  "post.restored": (p) => `Your post is back in the room — a moderator dismissed the report.`,
  "reply.to_your_post": (p) => `${p?.from ?? "A colleague"} replied to your case.`,
  "reply.to_thread": (p) => `${p?.from ?? "A colleague"} replied in a thread you're in.`,
  "reply.marked_helpful": () => "Your reply changed a colleague's management.",
};

function Notifications({ token, onBack, onOpenPost }) {
  const [list, setList] = useState({ state: "loading", items: [], error: null });

  const load = async () => {
    try {
      const result = await api("/api/notifications", { token });
      setList({ state: "ready", items: result.notifications ?? [], error: null });
    } catch (e) {
      setList({ state: "error", items: [], error: e.message });
    }
  };
  useEffect(() => { void load(); }, []);

  const read = async (item) => {
    if (item.read_at) return;
    try {
      await api(`/api/notifications/${item.id}/read`, { method: "POST", token, body: {} });
      setList((current) => ({
        ...current,
        items: current.items.map((n) =>
          n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n),
      }));
    } catch { /* leave it unread; tapping again retries */ }
  };

  return (
    <Shell>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={onBack} aria-label="Back to the room" className="rounded-lg p-1.5 hover:bg-slate-100">
          <ArrowLeft size={18} />
        </button>
        <p className="font-medium">Notifications</p>
      </header>
      <main className="p-2">
        {list.state === "loading" && <CenteredInline label="Loading notifications…" />}
        {list.state === "error" && (
          <p className="m-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{list.error}</p>
        )}
        {list.state === "ready" && list.items.length === 0 && (
          <p className="p-4 text-sm text-slate-500">Nothing yet. Replies and review results arrive here.</p>
        )}
        <ul>
          {list.items.map((item) => (
            <li key={item.id}>
              <button onClick={() => {
                  void read(item);
                  // The three reply kinds all point at a post; the reply id,
                  // when the payload carries one, is scrolled to on arrival.
                  // group.invited waits for the invitations screen (slice 3)
                  // rather than getting an invented destination.
                  const target = REPLY_KINDS.has(item.kind) ? item.payload?.postId : null;
                  if (target && onOpenPost) void onOpenPost(target, item.payload?.replyId ?? null);
                }}
                className={`flex w-full items-start gap-3 rounded-xl p-3 text-left hover:bg-slate-100 ${item.read_at ? "opacity-60" : ""}`}>
                <span aria-hidden="true"
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${item.read_at ? "bg-transparent" : "bg-teal-700"}`} />
                <span>
                  <span className="block text-sm leading-6">
                    {(NOTIFICATION_COPY[item.kind] ?? (() => item.kind))(item.payload)}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {new Date(item.deliver_after ?? item.created_at).toLocaleString()}
                    {item.read_at ? "" : " · unread"}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </main>
    </Shell>
  );
}

/* ========================================================================== */
/* Composer — the case, its restriction, its poll, its images                 */
/* ========================================================================== */

/**
 * The height a person can actually see. On iOS the software keyboard shrinks
 * the VISUAL viewport but not the layout viewport, and a `fixed inset-0`
 * dialog keeps its full height — so everything in its lower third sits under
 * the keyboard. The phone walkthrough found the poll control and the Post
 * button both there (finding 5): present, working, and unreachable at the one
 * moment they are needed, because the author has necessarily just typed the
 * case body and the keyboard is necessarily up.
 */
function useVisualHeight() {
  const [height, setHeight] = useState(() =>
    (typeof window !== "undefined" && window.visualViewport?.height) || null);
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return undefined;
    const update = () => setHeight(vv.height);
    vv.addEventListener("resize", update);
    return () => vv.removeEventListener("resize", update);
  }, []);
  return height;
}

function NotFound() {
  return (
    <Shell className="grid place-items-center p-6">
      <div className="max-w-sm text-center">
        <h1 className="font-serif text-2xl">Page not found</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">There's nothing at this address.</p>
        <a href="/" className="mt-4 inline-block rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white">
          Back to MedConnect
        </a>
      </div>
    </Shell>
  );
}

/* ========================================================================== */
/* Staff console                                                              */
/*                                                                            */
/* The D32 review queue and the D36 flag queue, drained where staff actually  */
/* are. Nothing here holds authority: every list and every decision is        */
/* require_staff-gated in SQL, and a non-staff caller gets the same 404 this  */
/* path itself gives them. The client's only judgement is what to OFFER       */
/* (D35): a reviewer sees reviews, a moderator flags, an admin both.          */
/* ========================================================================== */

const REVIEW_REASONS = {
  name_or_registry_mismatch: "Name does not match the registry record",
  npi_already_claimed: "NPI already bound to another account",
  institutional_email_unrecognized: "Email domain not a recognized institution",
  exclusion_data_stale: "Exclusion data was stale at verification",
  insufficient_signals: "Not enough independent signals",
  no_registry: "No machine-readable registry for this country",
  registry_down: "Registry was unreachable",
  no_institutional_email: "No institutional email",
  seniority_claim: "Seniority claim needs review",
};

const CLAIM_LABELS = {
  givenName: "Given name", familyName: "Family name", registry: "Registry",
  registryId: "Registry ID", registryMatched: "Registry matched",
  emailDomain: "Email domain", bound: "Invitation binding",
  country: "Country", institution: "Institution",
};

function ClaimList({ heading, data }) {
  const entries = Object.entries(data ?? {});
  if (entries.length === 0) return null;
  return (
    <div>
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{heading}</h4>
      <dl className="mt-1 space-y-0.5">
        {entries.map(([key, value]) => (
          <div key={key} className="grid grid-cols-[7rem_1fr] gap-2 text-xs">
            <dt className="text-slate-500">{CLAIM_LABELS[key] ?? key}</dt>
            <dd className="break-words font-mono text-slate-800">{String(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ReviewCard({ review, token, onDecided }) {
  const [open, setOpen] = useState(false);
  const [years, setYears] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const awaitingSecond = review.state === "awaiting_second";

  const decide = async (verdict) => {
    setError(null);
    if (verdict === "approved" && !/^\d+$/.test(years.trim())) {
      setError("Approval needs the years of independent practice the evidence supports.");
      return;
    }
    setBusy(true);
    try {
      const result = await api(`/api/admin/reviews/${review.id}/decision`, {
        method: "POST", token,
        body: { verdict, ...(verdict === "approved" ? { years: Number(years) } : {}),
                ...(reason.trim() ? { reason: reason.trim() } : {}) },
      });
      onDecided(review, result.state);
    } catch (e) {
      // The refusals worth reading come from the SQL itself: four-eyes, and
      // deciding your own credentials. Show them where the decision was made.
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="rounded-xl border border-slate-200 bg-white">
      <button onClick={() => setOpen((current) => !current)}
        className="flex w-full items-start justify-between gap-3 p-4 text-left">
        <span className="min-w-0">
          <span className="block truncate font-medium">{review.display_name}</span>
          <span className="mt-0.5 block text-xs text-slate-500">
            {REVIEW_REASONS[review.reason] ?? review.reason}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          {review.priority !== "normal" && (
            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-rose-700">
              {review.priority}
            </span>
          )}
          {awaitingSecond && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
              Awaiting a second reviewer
            </span>
          )}
          <span className="text-[10px] text-slate-400">
            {new Date(review.created_at).toLocaleDateString()}
          </span>
        </span>
      </button>
      {open && (
        <div className="space-y-4 border-t border-slate-100 p-4">
          <ClaimList heading="What they claim" data={review.claim} />
          <ClaimList heading="What the record shows" data={review.evidence} />
          {review.name_score != null && (
            <p className="text-xs text-slate-500">
              Name similarity against the registry: <span className="font-mono">{review.name_score}</span>
            </p>
          )}
          {awaitingSecond ? (
            <p className="text-xs leading-5 text-slate-600">
              A first reviewer has approved a senior grant. Your approval completes it;
              the same person cannot sign twice.
            </p>
          ) : (
            <p className="text-xs leading-5 text-slate-600">
              Approving 10 or more years sends this for a second reviewer rather than
              verifying immediately.
            </p>
          )}
          <div>
            <label htmlFor={`years-${review.id}`} className="text-xs font-medium text-slate-700">
              Years in independent practice
            </label>
            <input id={`years-${review.id}`} inputMode="numeric" value={years}
              onChange={(event) => setYears(event.target.value)}
              className="mt-1 w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label htmlFor={`reason-${review.id}`} className="text-xs font-medium text-slate-700">
              Reason
            </label>
            <textarea id={`reason-${review.id}`} rows={2} value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="What the decision rests on — it goes in the audit log."
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          {error && (
            <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => decide("approved")} disabled={busy}
              className="rounded-xl bg-teal-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              Approve
            </button>
            <button onClick={() => decide("rejected")} disabled={busy}
              className="rounded-xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 disabled:opacity-50">
              Reject
            </button>
            <button onClick={() => decide("escalated")} disabled={busy}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">
              Escalate
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

function ReviewQueue({ token }) {
  const [queue, setQueue] = useState({ state: "loading", reviews: [], error: null });
  const [notice, setNotice] = useState(null);

  const load = async () => {
    setQueue((current) => ({ ...current, state: "loading", error: null }));
    try {
      const result = await api("/api/admin/reviews", { token });
      setQueue({ state: "ready", reviews: result.reviews ?? [], error: null });
    } catch (e) {
      setQueue({ state: "error", reviews: [], error: e.message });
    }
  };
  useEffect(() => { void load(); }, []);

  const onDecided = (review, state) => {
    if (state === "awaiting_second") {
      setNotice(`${review.display_name} — approved once; a second reviewer completes it.`);
      setQueue((current) => ({
        ...current,
        reviews: current.reviews.map((row) =>
          row.id === review.id ? { ...row, state: "awaiting_second" } : row),
      }));
    } else {
      setNotice(`${review.display_name} — ${state}.`);
      setQueue((current) => ({
        ...current,
        reviews: current.reviews.filter((row) => row.id !== review.id),
      }));
    }
  };

  return (
    <section aria-label="Verification reviews" className="space-y-3">
      {notice && (
        <p role="status" className="rounded-xl border border-teal-200 bg-teal-50 p-3 text-xs text-teal-900">
          {notice}
        </p>
      )}
      {queue.state === "loading" && <CenteredInline label="Loading the review queue…" />}
      {queue.state === "error" && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {queue.error}<button onClick={load} className="ml-2 font-semibold underline">Retry</button>
        </div>
      )}
      {queue.state === "ready" && queue.reviews.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          The review queue is empty.
        </div>
      )}
      {queue.reviews.map((review) => (
        <ReviewCard key={review.id} review={review} token={token} onDecided={onDecided} />
      ))}
    </section>
  );
}

function FlagCard({ flag, token, onResolved }) {
  const [resolution, setResolution] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const resolve = async (uphold) => {
    setError(null);
    setBusy(true);
    try {
      await api(`/api/admin/flags/${flag.id}/resolution`, {
        method: "POST", token,
        body: { uphold, ...(resolution.trim() ? { resolution: resolution.trim() } : {}) },
      });
      onResolved(flag, uphold);
    } catch (e) {
      // resolve_flag's own refusal — a moderator resolving their own flag —
      // arrives here with its reason.
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-xs font-semibold ${flag.reason === "patient_info" ? "text-rose-700" : "text-slate-700"}`}>
            {flag.reason.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}
          </p>
          <p className="mt-0.5 truncate text-sm">{flag.post_title ?? "(a reply or message)"}</p>
          {flag.note && <p className="mt-1 text-xs leading-5 text-slate-600">"{flag.note}"</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {flag.auto_hidden && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
              Auto-hidden
            </span>
          )}
          <span className="text-[10px] text-slate-400">{new Date(flag.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <input aria-label="Resolution note" value={resolution} placeholder="Resolution note (optional)"
        onChange={(event) => setResolution(event.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      {error && (
        <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button onClick={() => resolve(true)} disabled={busy}
          className="rounded-xl bg-rose-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          Uphold
        </button>
        <button onClick={() => resolve(false)} disabled={busy}
          className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">
          Dismiss
        </button>
      </div>
    </article>
  );
}

function FlagQueue({ token }) {
  const [queue, setQueue] = useState({ state: "loading", flags: [], error: null });
  const [notice, setNotice] = useState(null);

  const load = async () => {
    setQueue((current) => ({ ...current, state: "loading", error: null }));
    try {
      const result = await api("/api/admin/flags", { token });
      setQueue({ state: "ready", flags: result.flags ?? [], error: null });
    } catch (e) {
      setQueue({ state: "error", flags: [], error: e.message });
    }
  };
  useEffect(() => { void load(); }, []);

  const onResolved = (flag, uphold) => {
    setNotice(uphold
      ? "Upheld — the content is removed."
      : "Dismissed — an auto-hidden post returns to the room.");
    setQueue((current) => ({
      ...current, flags: current.flags.filter((row) => row.id !== flag.id),
    }));
  };

  return (
    <section aria-label="Flag queue" className="space-y-3">
      {notice && (
        <p role="status" className="rounded-xl border border-teal-200 bg-teal-50 p-3 text-xs text-teal-900">
          {notice}
        </p>
      )}
      {queue.state === "loading" && <CenteredInline label="Loading the flag queue…" />}
      {queue.state === "error" && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {queue.error}<button onClick={load} className="ml-2 font-semibold underline">Retry</button>
        </div>
      )}
      {queue.state === "ready" && queue.flags.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          No open flags.
        </div>
      )}
      {queue.flags.map((flag) => (
        <FlagCard key={flag.id} flag={flag} token={token} onResolved={onResolved} />
      ))}
    </section>
  );
}

function StaffConsole({ token, me, onSignOut }) {
  // What to OFFER, not what is allowed — is_staff in SQL treats admin as
  // every role, and this mirrors that one rule. A tab this misjudged would
  // show an empty 404'd list, not grant anything.
  const tabs = me.staff_role === "admin"
    ? ["reviews", "flags"]
    : me.staff_role === "moderator" ? ["flags"] : ["reviews"];
  const [tab, setTab] = useState(tabs[0]);

  return (
    <Shell>
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <a href="/" aria-label="Back to the rooms"
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
              <ArrowLeft size={18} />
            </a>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-teal-800">
                {me.staff_role}
              </p>
              <h1 className="truncate font-serif text-xl">Staff console</h1>
            </div>
          </div>
          <QuietSignOut onClick={onSignOut} />
        </div>
        {tabs.length > 1 && (
          <nav className="mt-4 flex gap-1" aria-label="Queues">
            <button onClick={() => setTab("reviews")}
              className={`rounded-full px-3 py-1.5 text-xs font-medium ${tab === "reviews" ? "bg-slate-950 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              Reviews
            </button>
            <button onClick={() => setTab("flags")}
              className={`rounded-full px-3 py-1.5 text-xs font-medium ${tab === "flags" ? "bg-slate-950 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              Flags
            </button>
          </nav>
        )}
      </header>
      <main className="space-y-3 p-4 pb-24">
        {tab === "reviews" ? <ReviewQueue token={token} /> : <FlagQueue token={token} />}
      </main>
    </Shell>
  );
}

function Composer({ token, me, venueId, defaultKind, allowedKinds = KINDS, industry = false,
                    attachmentsEnabled = true, onClose, onCreated }) {
  const visualHeight = useVisualHeight();
  const [draft, setDraft] = useState({ kind: defaultKind, title: "", body: "", minBand: null });
  const [anonymous, setAnonymous] = useState(false);
  // Eligibility is fetched at OPEN, not read from the sign-in snapshot: the
  // count moves as the author posts, and a locked line showing the number
  // from three posts ago reads as broken arithmetic.
  const [eligibility, setEligibility] = useState(me?.anonymous_eligibility ?? null);
  useEffect(() => {
    let active = true;
    api("/api/me", { token })
      .then((result) => { if (active && result.anonymous_eligibility) setEligibility(result.anonymous_eligibility); })
      .catch(() => { /* the snapshot stands */ });
    return () => { active = false; };
  }, []);
  // The pre-submit scan (D14 via POST /api/posts/scan): null until run for
  // the current text; edits reset it. Blocks stop the post; warnings wait for
  // the author's explicit "Post anyway" — the point is a warning BEFORE the
  // risk is taken, and one after the post exists is too late to be one.
  const [scanReport, setScanReport] = useState(null);
  const [warningsAccepted, setWarningsAccepted] = useState(false);
  const [pollEnabled, setPollEnabled] = useState(false);
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [files, setFiles] = useState([]);
  const [mediaAttested, setMediaAttested] = useState(false);
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);

  // "compose" | "confirm". A post carrying media passes through the
  // confirmation step; one without goes straight out.
  const [stage, setStage] = useState("compose");

  // An author restricts to their own band or below (D3): offering higher bands
  // would draw a 422 the server was always going to send.
  const myBandIndex = BANDS.indexOf(me?.experience_band);
  const offerableBands = myBandIndex >= 0 ? BANDS.slice(0, myBandIndex + 1) : [];

  const editText = (next) => {
    setDraft(next);
    setScanReport(null);
    setWarningsAccepted(false);
  };

  const submit = async (event, { acceptWarnings = false } = {}) => {
    event.preventDefault();
    const warningsOk = warningsAccepted || acceptWarnings;

    // The pre-submit check, before anything is created or uploaded. A block
    // here is what the worker would hold anyway, told to the author while the
    // draft is still theirs; a warning is D14's "you might be identifying
    // yourself" — shown once, then the author decides. A scan failure or the
    // rate limit falls through to posting: the worker remains the
    // enforcement, this is the courtesy in front of it.
    if (!scanReport) {
      try {
        const report = await api("/api/posts/scan", {
          method: "POST", token,
          body: {
            title: draft.title, body: draft.body,
            ...(pollEnabled ? { pollOptions: pollOptions.map((option) => option.trim()) } : {}),
          },
        });
        setScanReport(report);
        if (report.blocked) return;
        if (report.warnings?.length > 0 && !warningsOk) return;
      } catch { setScanReport({ blocked: false, blocks: [], warnings: [], unavailable: true }); }
    } else {
      if (scanReport.blocked) return;
      if (scanReport.warnings?.length > 0 && !warningsOk) return;
    }

    // Attested media skips the burned-in text scan, so this confirmation is the
    // only author-side control over what is visible in the frame. Showing the
    // frames at the moment of attesting is the point: the realistic failure is
    // not a physician who does not care, it is one who checked seven images and
    // attested to eight.
    if (files.length > 0 && stage === "compose") {
      setStage("confirm");
      return;
    }
    if (files.length > 0 && !mediaAttested) return;

    setState("sending"); setError(null);
    const uploaded = [];
    try {
      for (const file of files) uploaded.push(await uploadMedia(file, token));
      const created = await api("/api/posts", {
        method: "POST", token,
        body: {
          venueId, ...draft,
          ...(anonymous ? { anonymous: true } : {}),
          ...(pollEnabled ? { pollOptions: pollOptions.map((option) => option.trim()) } : {}),
          ...(uploaded.length ? {
            attachmentIds: uploaded.map((item) => item.id),
            mediaAttested: true,
          } : {}),
        },
      });
      onCreated(created, draft);
    } catch (e) {
      await Promise.all(uploaded.map((item) => api(`/api/uploads/${item.id}`, {
        method: "DELETE", token,
      }).catch(() => {})));
      /*
       * Stay where the author is standing.
       *
       * This used to drop back to the compose stage and set an error that
       * rendered at the very bottom of a scrollable column — measured at
       * y=848 in an 844px viewport on a phone. So the author tapped Post, the
       * attestation screen vanished, nothing else changed, and the only
       * explanation was off-screen. "Post does nothing" was a real and
       * complete description of what they could see.
       *
       * With files attached the failure belongs on the attestation screen,
       * beside the button that was pressed. The attestation itself still
       * stands: the file set has not changed, so what they confirmed about
       * those frames is still true.
       */
      setError(e.message);
      setState("idle");
      if (files.length === 0) setStage("compose");
    }
  };

  if (stage === "confirm") {
    return (
      <MediaAttestation
        files={files}
        busy={state === "sending"}
        error={error}
        attested={mediaAttested}
        onAttest={setMediaAttested}
        onBack={() => { setStage("compose"); setMediaAttested(false); }}
        onConfirm={submit}
      />
    );
  }

  const pollAllowed = draft.kind !== "job";
  return (
    <div className="fixed inset-0 z-40 bg-slate-950/40 sm:grid sm:place-items-center sm:p-3"
      role="dialog" aria-modal="true" aria-label="Create post">
      <form onSubmit={submit}
        style={visualHeight ? { maxHeight: `${visualHeight}px` } : undefined}
        className="mx-auto flex h-full w-full flex-col bg-white sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-2xl sm:shadow-2xl">
        <div className="flex items-center gap-3 px-5 pt-5">
          <button type="button" onClick={onClose} aria-label="Close composer"><ArrowLeft size={20} /></button>
          <h2 className="font-serif text-xl">New post</h2>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-4 pt-5">
          <select value={draft.kind} onChange={(event) => {
            const kind = event.target.value;
            setDraft({ ...draft, kind });
            if (kind === "job") setPollEnabled(false);
          }} aria-label="Post type" className="w-full rounded-xl border border-slate-300 px-3 py-2.5">
            {allowedKinds.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <input required value={draft.title} onChange={(event) => editText({ ...draft, title: event.target.value })}
            placeholder="The question you want answered"
            className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none ring-teal-700 focus:ring-2" />
          <textarea required rows={7} value={draft.body} onChange={(event) => editText({ ...draft, body: event.target.value })}
            placeholder={industry ? "Research context, disclosure, or job details" : "Clinical context without names, dates, record numbers, or other identifiers"}
            className="w-full resize-none rounded-xl border border-slate-300 px-4 py-3 outline-none ring-teal-700 focus:ring-2" />

          {!industry && offerableBands.length > 0 && (
            <label className="block text-sm font-medium" htmlFor="minBand">
              Who can read this
              <span className="mt-1 block text-xs font-normal text-slate-500">
                You can restrict up to your own experience band — yours is{" "}
                {BAND_LABEL[me?.experience_band] ?? me?.experience_band}. Higher
                bands aren't offered because the room would refuse them.
              </span>
              <select id="minBand" value={draft.minBand ?? ""}
                onChange={(event) => setDraft({ ...draft, minBand: event.target.value || null })}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-normal">
                <option value="">Everyone in the room</option>
                {offerableBands.filter((band) => band !== "training").map((band) => (
                  <option key={band} value={band}>{BAND_LABEL[band]} and above</option>
                ))}
              </select>
              <span className="mt-1 block text-xs font-normal text-slate-500">
                Restricted posts are hidden from readers below the band — they see a count, never the title.
              </span>
            </label>
          )}

          {!industry && draft.kind === "case" && (() => {
            if (!eligibility) return null;
            const locked = !eligibility.eligible && eligibility.signed_posts < eligibility.threshold;
            const weeklyOut = eligibility.eligible === false && eligibility.signed_posts >= eligibility.threshold
              && eligibility.remaining_this_week === 0;
            return (
              <div className="rounded-xl border border-slate-200 p-3">
                {locked ? (
                  // Rendered locked WITH the count, never hidden until earned
                  // (walkthrough finding 9): a control that appears from
                  // nowhere at post three reads as a glitch, not a feature.
                  <p className="flex items-center gap-2 text-sm text-slate-500">
                    <Lock size={15} className="shrink-0" />
                    <span>
                      <span className="font-medium">Post anonymously</span> — unlocks
                      after {eligibility.threshold} signed posts — {eligibility.signed_posts} of {eligibility.threshold}.
                    </span>
                  </p>
                ) : (
                  <>
                    <label className="flex cursor-pointer items-start gap-2 text-sm">
                      <input type="checkbox" className="mt-1" checked={anonymous}
                        disabled={weeklyOut}
                        onChange={(event) => setAnonymous(event.target.checked)} />
                      <span>
                        <span className="font-medium">Post anonymously</span>
                        <span className="mt-0.5 block text-xs leading-5 text-slate-500">
                          Anonymous to readers, not to the platform. Staff can
                          unmask with cause, and in a small room your writing
                          and the case itself can still identify you.
                        </span>
                      </span>
                    </label>
                    {weeklyOut && (
                      <p className="mt-2 text-xs text-slate-500">
                        You've used this week's two anonymous posts — the next unlocks as the week turns.
                      </p>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {attachmentsEnabled && !industry && (
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-teal-800">
                  <ImagePlus size={17} /> Add images
                  <input type="file" multiple className="sr-only"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/dicom,.dcm,.heic,.heif"
                    onChange={(event) => {
                      const selected = Array.from(event.target.files ?? []);
                      if (files.length + selected.length > 8) { setError("You can attach up to eight files."); return; }
                      const tooLarge = selected.find((file) => file.size > 25_000_000);
                      if (tooLarge) { setError(`${tooLarge.name} is larger than 25 MB.`); return; }
                      setFiles((current) => [...current, ...selected]);
                      // A changed set withdraws any attestation already given.
                      setMediaAttested(false);
                      setError(null);
                      event.target.value = "";
                    }} />
                </label>
                <span className="text-xs text-slate-500">{files.length}/8 · 25 MB each</span>
              </div>
              {files.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {files.map((file, index) => (
                    <li key={`${file.name}-${index}`} className="flex items-center justify-between gap-2 text-xs text-slate-600">
                      <span className="truncate">{file.name}</span>
                      <button type="button" aria-label={`Remove ${file.name}`}
                        onClick={() => { setFiles(files.filter((_, i) => i !== index)); setMediaAttested(false); }}
                        className="rounded p-1 hover:bg-slate-100"><X size={13} /></button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-xs leading-5 text-slate-500">
                Images only in this pilot. You'll review every frame and attest before posting.
              </p>
            </div>
          )}

          {pollEnabled && (
            <fieldset className="rounded-xl border border-slate-200 p-3">
              <legend className="px-1 text-sm font-medium">Poll</legend>
              <div className="space-y-2">
                {pollOptions.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <label className="sr-only" htmlFor={`poll-${index}`}>{`Poll option ${index + 1}`}</label>
                    <input id={`poll-${index}`} required value={option} maxLength={160}
                      aria-label={`Poll option ${index + 1}`}
                      onChange={(event) => {
                        setPollOptions(pollOptions.map((o, i) => i === index ? event.target.value : o));
                        setScanReport(null); setWarningsAccepted(false);
                      }}
                      placeholder={`Option ${index + 1}`}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                    {pollOptions.length > 2 && (
                      <button type="button" aria-label={`Remove option ${index + 1}`}
                        onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== index))}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between">
                {pollOptions.length < 6 ? (
                  <button type="button" onClick={() => setPollOptions([...pollOptions, ""])}
                    className="text-xs font-medium text-teal-800">Add option</button>
                ) : <span className="text-xs text-slate-400">Six options is the ceiling</span>}
                <button type="button" onClick={() => { setPollEnabled(false); setPollOptions(["", ""]); }}
                  className="text-xs text-slate-500">Remove poll</button>
              </div>
              <p className="mt-2 text-xs text-slate-500">Votes are private. The room sees totals, never voters.</p>
            </fieldset>
          )}

          {scanReport?.blocked && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm" role="alert">
              <p className="font-medium text-rose-900">This can't post as written.</p>
              <p className="mt-1 leading-6 text-slate-700">
                The check found{" "}
                {[...new Set([...(scanReport.blocks ?? []).map((b) => b.detector), ...(scanReport.pollBlocks ?? [])])]
                  .map(detectorLabel).join(", ")}.
                Take the identifier out and post — the room never sees drafts.
              </p>
            </div>
          )}
          {!scanReport?.blocked && scanReport?.warnings?.length > 0 && !warningsAccepted && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm" role="alert">
              <p className="font-medium text-amber-900">This might identify you.</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-slate-700">
                {scanReport.warnings.map((warning, index) => (
                  <li key={index}>{warning.label}</li>
                ))}
              </ul>
              <p className="mt-2 text-xs leading-5 text-slate-600">
                Nothing here blocks the post — the call is yours. It matters
                most on an anonymous post, where the details are the signature.
              </p>
              <button type="button"
                onClick={(event) => {
                  setWarningsAccepted(true);
                  void submit({ preventDefault() {}, ...event }, { acceptWarnings: true });
                }}
                className="mt-2 rounded-lg bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white">
                Post anyway
              </button>
            </div>
          )}
          {error && <p className="text-sm text-rose-700" role="alert">{error}</p>}
        </div>
        {/*
          * The action bar lives OUTSIDE the scroll region, at the bottom of a
          * form whose max-height tracks the visual viewport — so "Add poll"
          * and "Post" stay on screen with the keyboard up, at 44px targets,
          * instead of 28px text buttons under the keyboard.
          */}
        <div className="flex items-stretch gap-2 border-t border-slate-200 px-5 py-3">
          {pollAllowed && !pollEnabled && (
            <button type="button" onClick={() => setPollEnabled(true)}
              className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-teal-700 px-4 text-sm font-semibold text-teal-800">
              <Plus size={16} /> Add poll
            </button>
          )}
          <button disabled={state === "sending"}
            className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white disabled:opacity-50">
            {state === "sending" && <LoaderCircle className="animate-spin" size={16} />}
            {files.length > 0 ? `Review ${files.length} image${files.length === 1 ? "" : "s"} and post` : "Post"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * The attestation, as D10 designed it and D48 kept it: one attestation covering
 * the batch, given while every frame is on screen at a readable size, worded as
 * an act performed. Two properties are load-bearing here:
 *
 *   - The checkbox stays disabled until every image has actually been in the
 *     viewport. A physician cannot attest to a frame the screen never showed
 *     them — checked seven, attested to eight is the failure this exists for.
 *   - Changing the file set withdraws the attestation (the composer resets it),
 *     because a confirmation given for one set of pixels does not transfer.
 */
function MediaAttestation({ files, busy, error, attested, onAttest, onBack, onConfirm }) {
  const urls = useMemo(() => files.map((file) => URL.createObjectURL(file)), [files]);
  useEffect(() => () => { urls.forEach((url) => URL.revokeObjectURL(url)); }, [urls]);

  const [viewed, setViewed] = useState(() => new Set());
  const allViewed = viewed.size >= files.length;
  const refs = useRef([]);
  /*
   * A frame counts as viewed only once it has LOADED and been in the viewport.
   * Before an object-URL image decodes it has zero height, so on a slow decode
   * every zero-height figure "fits" in the viewport at once and intersection
   * alone would mark all of them viewed — an attestation gate defeated by a
   * slow network. Caught by CI's runner being slower than a laptop.
   */
  const loadedRef = useRef(new Set());
  const intersectingRef = useRef(new Set());
  /*
   * A frame the browser cannot render.
   *
   * The gate requires every frame to have been seen, so a file that never
   * decodes leaves the checkbox disabled forever — correctly, since nobody can
   * attest to a picture they were never shown, but SILENTLY. On a phone that
   * is indistinguishable from the button being broken, which is most of what
   * "Post does nothing" meant. Name the file and say what to do instead.
   */
  const [failed, setFailed] = useState(() => new Set());

  const markViewed = (index) => {
    setViewed((current) => {
      if (current.has(index)) return current;
      const next = new Set(current);
      next.add(index);
      return next;
    });
  };
  const onFrameLoad = (index) => {
    loadedRef.current.add(index);
    if (intersectingRef.current.has(index)) markViewed(index);
  };
  const onFrameError = (index) => setFailed((current) => {
    if (current.has(index)) return current;
    const next = new Set(current);
    next.add(index);
    return next;
  });

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      // No observer, no gate to hide behind: everything stays unviewed and the
      // attestation stays shut. In practice every supported browser has it.
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const index = Number(entry.target.dataset.index);
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          intersectingRef.current.add(index);
          if (loadedRef.current.has(index)) markViewed(index);
        } else {
          intersectingRef.current.delete(index);
        }
      }
    }, { threshold: [0.5] });
    refs.current.forEach((node) => { if (node) observer.observe(node); });
    return () => observer.disconnect();
  }, [files.length]);

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-white" role="dialog" aria-modal="true" aria-label="Review your images">
      <div className="mx-auto max-w-lg p-5 pb-40">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onBack} aria-label="Back to the composer"><ArrowLeft size={20} /></button>
          <h2 className="font-serif text-xl">Look at every frame</h2>
        </div>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          These images post exactly as they are. Metadata is removed and the pixels
          are checked for burned-in text, but nothing checks what is visible in the
          picture the way you can — a face, a screen in the background, a wristband.
        </p>
        <div className="mt-5 space-y-4">
          {files.map((file, index) => (
            <figure key={index} ref={(node) => { refs.current[index] = node; }} data-index={index}
              className="rounded-xl border border-slate-200 p-2">
              <img src={urls[index]} alt={`Image ${index + 1} of ${files.length}: ${file.name}`}
                onLoad={() => onFrameLoad(index)}
                onError={() => onFrameError(index)}
                className="min-h-40 w-full rounded-lg bg-slate-100 object-contain" />
              {failed.has(index) && (
                <p className="mt-1 rounded-lg bg-amber-50 p-2 text-xs leading-5 text-amber-900">
                  This browser can't display {file.name}, so you can't check it —
                  and you shouldn't attest to a picture you haven't seen. Remove
                  it and attach a JPEG or PNG instead.
                </p>
              )}
              <figcaption className="mt-1.5 flex items-center justify-between px-1 text-xs text-slate-500">
                <span className="truncate">{index + 1} of {files.length} · {file.name}</span>
                {viewed.has(index) && <CheckCircle2 className="shrink-0 text-teal-700" size={14} aria-label="Viewed" />}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto max-w-lg space-y-3 p-4">
          {!allViewed && (
            <p className="text-xs text-slate-500" role="status">
              Scroll through every image first — {viewed.size} of {files.length} viewed.
            </p>
          )}
          <label className={`flex items-start gap-2 text-sm leading-6 ${allViewed ? "" : "opacity-50"}`}>
            <input type="checkbox" checked={attested} disabled={!allViewed}
              onChange={(event) => onAttest(event.target.checked)} className="mt-1" />
            <span>
              I have looked at all {files.length} file{files.length === 1 ? "" : "s"}, and no
              patient name, date, record number, or face is visible in any frame.
            </span>
          </label>
          {failed.size > 0 && (
            <p className="text-sm text-amber-900" role="alert">
              {failed.size === 1 ? "One image" : `${failed.size} images`} couldn't be
              displayed, so {failed.size === 1 ? "it" : "they"} can't be attested to.
              Go back and remove {failed.size === 1 ? "it" : "them"}.
            </p>
          )}
          {error && <p className="text-sm text-rose-700" role="alert">{error}</p>}
          <button type="button" onClick={onConfirm} disabled={!attested || busy}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
            {busy && <LoaderCircle className="animate-spin" size={16} />}
            Post with {files.length} attested image{files.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}
